// CO2 Data viewer
// Written by Philip Levy phil-levy@outlook.com
// June 2026

import './style.css';
import {Map, Tile, View} from 'ol';
import {unByKey} from 'ol/Observable';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import {createXYZ} from 'ol/tilegrid';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import Circle from 'ol/style/Circle';
import Polygon from 'ol/geom/Polygon';
import RegularShape from 'ol/style/RegularShape';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Icon from 'ol/style/Icon';
import XYZ from 'ol/source/XYZ';
import {fromLonLat, toLonLat} from 'ol/proj';
import Overlay from 'ol/Overlay';
import LineString from 'ol/geom/LineString';
import Control from 'ol/control/Control.js';
import {defaults as defaultControls} from 'ol/control/defaults.js';
import Layer from 'ol/layer/WebGLTile.js';
import Google from 'ol/source/Google.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import tzLookup from 'tz-lookup';
import {getWindDirection, createArrowFeature} from './arrows.js';
import {selectNearestChlFile, listChlEntries, parseChlCsvRows, computeChlRange, normalizeChl, formatChlLabel} from './chl-layer.js';
import {createControlBand} from './controlband.js'; 
import {createMeasureToolManager, parseMeasureLineQueryValue, MEASURE_MAX_LINES} from './measure_tool.js';
import {GraphTool} from './graphs.js';

import MousePosition from 'ol/control/MousePosition';
import {createStringXY} from 'ol/coordinate';

import Graticule from 'ol/layer/Graticule';

let chl_folder = "occci"  // chl or occci
let show_full_orbits = true; // Set to false to show only the bounding box.  Not implemented yet.
let show_chl = false; // Set to false to hide chlorophyll circles
let show_sla = false; // Set to false to hide sea level anomaly circles
let show_wind_arrows = false; // Set to false to hide wind direction arrows
let show_grid_wind_arrows = false; // Set to false to hide the area-wide/RSS grid wind arrows
let show_satellite_view = false; // Set to true to show satellite view instead of roadmap
let show_orbit_labels = true; // Set to true to show orbit date labels
let show_split_color_spectrum = false; // Set to true to use a split color spectrum for delta values (blue for negative, red for positive) instead of a continuous spectrum
let exclude_edges = true; // Set to true to exclude edge points from average displays (raw_avg25, raw_avg12, delta_avg25, delta_avg12)
let show_clouds = false; // Set to true to show NASA Corrected Reflectance true-color clouds as a background layer
let mark_iffy_points = false; // Set to true to overlay ?/X marks on potentially cloudy points
let show_current_arrows = false; // Set to true to show ocean current arrows (independent of CHL and vorticity overlays)
let show_land_points = false; // Set to false to hide points where land_water_indicator is 0
let show_oco2 = true; // Set to false to keep only rows where satellite_id is 2
let show_oco3 = true; // Set to false to keep only rows where satellite_id is 3
let show_socat = false; // Set to false to filter out rows where satellite_id is 4
let show_vorticity = false; // Placeholder for future vorticity toggle support
let show_bga = false; // Set to true to show Blue-Green Algae overlay
let show_mblh = false; // Set to true to show the ERA5 Marine Boundary Layer Height overlay
let show_peaks = false; // Set to true to show the Maximum.png icon on peak orbit points
let show_valleys = false; // Set to true to show the Minimum.png icon and wide line on valley orbit points
let use_iffy_filter_files = false; // Set to true to load the "_exp" filtered variant of raw/averaged/gradient data files

export let show_selection = "raw_avg25"; // Options: "raw", "delta", "raw_avg12", "delta_avg12" , etc.
let show_location = "loading..."; // Default location to show in dropdown.  Will be overridden by available files if scanForDataFiles() is used.
let messageText = ""; // Message to display in the control band 
let date_range_start = null;
let date_range_end = null;
let date_range_window = 14;
let date_range_min = null;
let date_range_max = null;
let value_filter_absolute_min = null;
let value_filter_absolute_max = null;
let value_filter_min = null;
let value_filter_max = null;
let value_filter_unit = '';
let value_filter_decimals = 2;
const remembered_value_filter_ranges = {
  co2: null,
  gradient: null
};
let last_loaded_location = null;
let last_shown_cloud_date = null; // Track the last date for which clouds were shown to avoid unnecessary reloads
let last_vorticity_date = null; // Track the actual vorticity file date loaded (YYYY-MM-DD), used for popup date-gating
let last_chl_data_date = null; // Track the date for which CHL data was loaded (YYYY-MM-DD)
let last_bga_data_date = null; // Track the date for which BGA data was loaded (YYYY-MM-DD)
let feature_display_date = null; // Single shared date driving Clouds, Currents, Vorticity, Wind, CHL, and BGA displays
let chlManifestLocation = null; // Location currently cached for CHL manifest text
let chlManifestTextCache = null; // Cached CHL manifest content for nearest-date matching
let chlNearestDateCache = new globalThis.Map(); // Cache nearest CHL date per (location, orbit-date)
let earliest_displayed_orbit_date = null; // Track earliest displayed orbit date so checkbox can load matching vorticity
let current_chl_label = null; // Track CHL file/date label currently being shown
let current_spectrum_metric_range = null; // Track active main-spectrum metric range for cursor marker placement.
let current_vorticity_range = null; // Track active vorticity range for cursor marker placement.
const VORTICITY_SPECTRUM_MIN = -12e-6; // Fixed spectrum lower bound (-15 x E-6).
const VORTICITY_SPECTRUM_MAX = 12e-6; // Fixed spectrum upper bound (+15 x E-6).
const CHL_SPECTRUM_BOUNDS = [0.01, 0.1, 0.17, 0.3, 0.5, 0.6, 1.0, 1.7, 3.0, 5.0];
const CHL_SPECTRUM_LABELS = ['0.01', '0.1', '0.17', '0.3', '0.5', '0.6', '1', '1.7', '3', '5+'];
const CHL_SPECTRUM_COLORS = [
  '#d9f5da',
  '#c6eec8',
  '#b2e6b5',
  '#9fde9f',
  '#89d788',
  '#73cd70',
  '#5cbf59',
  '#439f42',
  '#2d7a2b'
];
// const BGA_SPECTRUM_CONFIG = [
//   {min: -Infinity, max: 0, color: '#F0F6FE', label: '<0'},
//   {min: 0, max: .1, color: '#E0EEFD', label: '0'},
//   {min: 0.1, max: 0.5, color: '#D0E5FC', label: '0.1'},
//   {min: 0.5, max: 1, color: '#C0DDFA', label: '0.5'},
//   {min: 1, max: 5, color: '#B0D4F9', label: '1'},
//   {min: 5, max: 10, color: '#9ECCF8', label: '5'},
//   {min: 10, max: 50, color: '#8CC4F6', label: '10'},
//   {min: 50, max: 100, color: '#79BBF5', label: '50'},
//   {min: 100, max: 200, color: '#63B3F3', label: '100'},
//   {min: 200, max: 500, color: '#48ABF2', label: '200'},
//   {min: 500, max: Infinity, color: '#1DA3F0', label: '> 500'}
// ];
const BGA_SPECTRUM_CONFIG = [
  {min: -Infinity, max: 0, color: '#F0F6FE', label: '<0'},
  {min: 0, max: 10, color: '#E0EEFD', label: '0'},
  {min: 10, max: 100, color: '#D0E5FC', label: '10'},
  {min: 100, max: 200, color: '#C0DDFA', label: '100'},
  {min: 200, max: 250, color: '#B0D4F9', label: '200'},
  {min: 250, max: 300, color: '#9ECCF8', label: '250'},
  {min: 300, max: 350, color: '#8CC4F6', label: '300'},
  {min: 350, max: 400, color: '#79BBF5', label: '350'},
  {min: 400, max: 450, color: '#63B3F3', label: '400'},
  {min: 450, max: 500, color: '#48ABF2', label: '450'},
  {min: 500, max: 1000, color: '#1DA3F0', label: '500'},
  {min: 1000, max: Infinity, color: '#0b89d2', label: '1000+'}
];

const MBLH_MIN = 300; // meters; values below are drawn black
const MBLH_MAX = 1200; // meters; values above are drawn white
const MBLH_TIMES = ['0900', '1100', '1300', '1500']; // local ERA5 columns mblh<time>
const MBLH_DEFAULT_TIME = '1300';
let mblh_time = MBLH_DEFAULT_TIME;

const formatMblhTime = (timeText) => `${timeText.slice(0, 2)}:${timeText.slice(2)}`;

// Accepts "1100" or "11:00"; anything else falls back to the default.
const normalizeMblhTime = (rawValue) => {
  const compact = typeof rawValue === 'string' ? rawValue.trim().replace(':', '') : '';
  return MBLH_TIMES.includes(compact) ? compact : MBLH_DEFAULT_TIME;
};
const MBLH_SPECTRUM_COLORS = [
  '#d4f5c4', '#b5ebb8', '#8fdfb4', '#6dd3bc', '#52c0c4', '#41a6c8', '#3a88c4', '#3568b5', '#2c4a9a'
];

function getMblhColor(value) {
  if (value < MBLH_MIN) return '#edfbc6f1';
  if (value > MBLH_MAX) return '#190665';
  const step = Math.floor((value - MBLH_MIN) / ((MBLH_MAX - MBLH_MIN) / MBLH_SPECTRUM_COLORS.length));
  return MBLH_SPECTRUM_COLORS[Math.min(MBLH_SPECTRUM_COLORS.length - 1, step)];
}

// Clicked points tracking
let clickedPoints = []; // Array to track clicked circle features
const maxClickedPoints = 10;
let selectedPopupFeature = null; // Track which feature's popup is currently selected
let isPopupPersisted = false; // Track whether popup is persisted (sticky) or just shown on hover


// CSV parser function
function parseCSV(csvText) {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',');
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    const obj = {};
    const currentLine = line.split(',');
    
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j].trim()] = currentLine[j].trim();
    }
    data.push(obj);
  }
  
  return data;
}

function parseDateFromRow(row) {
  if (row.sounding_id && row.sounding_id.length >= 8) {
    const datePart = row.sounding_id.substring(0, 8);
    const year = parseInt(datePart.substring(0, 4), 10);
    const month = parseInt(datePart.substring(4, 6), 10);
    const day = parseInt(datePart.substring(6, 8), 10);

    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      return new Date(year, month - 1, day);
    }
  }

  if (row.date || row.datetime || row.timestamp) {
    const parsed = new Date(row.date || row.datetime || row.timestamp);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
}

function parseDateFromSoundingId(soundingId) {
  if (!soundingId || soundingId.length < 8) return null;
  const datePart = soundingId.substring(0, 8);
  const year = parseInt(datePart.substring(0, 4), 10);
  const month = parseInt(datePart.substring(4, 6), 10);
  const day = parseInt(datePart.substring(6, 8), 10);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  return new Date(year, month - 1, day);
}

function parseUtcDateTimeFromSoundingId(soundingId) {
  // Step 1: parse the UTC date/time components embedded in sounding_id.
  if (!soundingId || soundingId.length < 12) return null;
  const year = parseInt(soundingId.substring(0, 4), 10);
  const month = parseInt(soundingId.substring(4, 6), 10);
  const day = parseInt(soundingId.substring(6, 8), 10);
  const hour = parseInt(soundingId.substring(8, 10), 10);
  const minute = parseInt(soundingId.substring(10, 12), 10);

  if ([year, month, day, hour, minute].some(Number.isNaN)) {
    return null;
  }

  // Step 2: construct a UTC Date so local timezone conversion is deterministic.
  return new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
}

function formatLocalTimestampForPoint(soundingId, latitudeValue, longitudeValue) {
  // Step 1: parse a UTC timestamp from sounding_id.
  const utcDate = parseUtcDateTimeFromSoundingId(soundingId);
  if (!utcDate) return 'N/A';

  // Step 2: parse coordinates used to resolve the point's IANA timezone.
  const lat = toFiniteNumber(latitudeValue);
  const lon = toFiniteNumber(longitudeValue);
  if (lat === null || lon === null) {
    return utcDate.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  }

  try {
    // Step 3: resolve timezone and format timestamp in that local timezone.
    const timeZone = tzLookup(lat, lon);
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });
    return `${formatter.format(utcDate)}`;   // removed (${timeZone}) because too long.
  } catch (_error) {
    // Step 4: fall back to UTC text if timezone lookup fails unexpectedly.
    return utcDate.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  }
}

function addDays(date, days) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function diffDays(startDate, endDate) {
  return Math.round((endDate - startDate) / 86400000);
}

function formatDateAsYYYYMMDD(dateValue) {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return null;
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Convert a Date to YYYY-MM-DD.
 * @param {Date} dateValue Date instance to format.
 * @returns {string|null} Formatted date string, or null for invalid input.
 */
function formatDateAsYYYYMMDDWithHyphens(dateValue) {
  const ymd = formatDateAsYYYYMMDD(dateValue);
  if (!ymd) return null;
  return `${ymd.substring(0, 4)}-${ymd.substring(4, 6)}-${ymd.substring(6, 8)}`;
}

/**
 * Convert a compact YYYYMMDD string to YYYY-MM-DD.
 * @param {string} dateText 8-character date text (YYYYMMDD).
 * @returns {string|null} Hyphenated date text, or null if the input is invalid.
 */
function formatYYYYMMDDTextWithHyphens(dateText) {
  if (typeof dateText !== 'string' || dateText.length !== 8) return null;
  return `${dateText.substring(0, 4)}-${dateText.substring(4, 6)}-${dateText.substring(6, 8)}`;
}

/**
 * Parse a YYYY-MM-DD date text from wind_data.csv into a YYYYMMDD string.
 * @param {string} timeText Date text from the "time" column.
 * @returns {string|null} YYYYMMDD text, or null if parsing fails.
 */
function parseWindDateToYYYYMMDD(timeText) {
  if (typeof timeText !== 'string') return null;
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(timeText.trim());
  if (!match) return null;
  const year = match[1];
  const month = match[2].padStart(2, '0');
  const day = match[3].padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Parse YYYY-MM-DD text to a local Date object.
 * @param {string} dateText Date text in YYYY-MM-DD format.
 * @returns {Date|null} Parsed Date, or null if parsing fails.
 */
function parseYYYYMMDDWithHyphens(dateText) {
  if (typeof dateText !== 'string') return null;
  const parts = dateText.split('-');
  if (parts.length !== 3) return null;

  const year = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);
  const day = Number.parseInt(parts[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Load and cache CHL manifest text for a location.
 * @param {string} location Active location name used in the data path.
 * @returns {Promise<string|null>} Manifest text, or null when unavailable.
 */
async function ensureChlManifestText(location) {
  // Step 1: validate input and reuse cached manifest for the same location.
  if (!location) return null;

  if (chlManifestTextCache && chlManifestLocation === location) {
    return chlManifestTextCache;
  }

  // Step 2: fetch manifest and reset nearest-date cache when location changes.
  const manifestPath = `./data/${location}/${chl_folder}/chl_days.txt`;
  const manifestResponse = await fetch(manifestPath);
  if (!manifestResponse.ok) {
    return null;
  }

  chlManifestTextCache = await manifestResponse.text();
  chlManifestLocation = location;
  chlNearestDateCache = new globalThis.Map();
  return chlManifestTextCache;
}

/**
 * Resolve the nearest CHL file date for a CO2 point date using cached manifest data.
 * @param {string} pointDateText Point/orbit date in YYYY-MM-DD.
 * @param {string} location Active location name used for cache scoping.
 * @returns {string|null} Nearest CHL date in YYYY-MM-DD, or null if unavailable.
 */
function getNearestChlDateForPointDate(pointDateText, location) {
  // Step 1: ensure we have manifest data for this location.
  if (!pointDateText || !location || !chlManifestTextCache || chlManifestLocation !== location) {
    return null;
  }

  // Step 2: use memoized nearest-date result when present.
  const cacheKey = `${location}|${pointDateText}`;
  if (chlNearestDateCache.has(cacheKey)) {
    return chlNearestDateCache.get(cacheKey);
  }

  // Step 3: compute and cache nearest CHL date for this point date.
  const pointDate = parseYYYYMMDDWithHyphens(pointDateText);
  if (!pointDate) {
    chlNearestDateCache.set(cacheKey, null);
    return null;
  }

  const selectedFile = selectNearestChlFile(chlManifestTextCache, pointDate);
  const nearestDateText = selectedFile ? formatYYYYMMDDTextWithHyphens(selectedFile.dateText) : null;
  chlNearestDateCache.set(cacheKey, nearestDateText);
  return nearestDateText;
}

/**
 * Fetch a CSV as text, falling back to "<url>.gz" when the plain file is missing and gunzipping gzip payloads.
 * @param {string} url Path of the uncompressed CSV file.
 * @returns {Promise<string|null>} File text, or null when neither the plain nor the .gz file could be loaded.
 */
async function fetchCsvText(url) {
  let response = await fetch(url);
  if (!response.ok) {
    response = await fetch(`${url}.gz`);
    if (!response.ok) return null;
  }

  // Detect gzip by magic bytes: servers differ on Content-Type/Content-Encoding for .gz files.
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).text();
  }
  return new TextDecoder().decode(bytes);
}

// Cache of parsed Chl CSV rows keyed by "location|filename" to avoid re-fetching the same file for the graph tool.
const chlFileRowsCache = new globalThis.Map();

async function loadChlFileRows(location, selectedFile) {
  const cacheKey = `${location}|${selectedFile.filename}`;
  if (chlFileRowsCache.has(cacheKey)) {
    return chlFileRowsCache.get(cacheKey);
  }

  let rows = [];
  try {
    const csvText = await fetchCsvText(`./data/${location}/${chl_folder}/${selectedFile.filename}`);
    if (csvText !== null) {
      rows = parseChlCsvRows(csvText);
    }
  } catch (error) {
    console.warn('Chl file load failed:', error);
    rows = [];
  }
  chlFileRowsCache.set(cacheKey, rows);
  return rows;
}

function haversineDistanceKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(a));
}

// Cache of parsed vorticity rows keyed by "location|YYYYMMDD" so the graph tool can reuse a date's file across points
// without disturbing the single-slot popup/as-of vorticity caches used by other displays.
const vorticityFileRowsCache = new globalThis.Map();

/**
 * Build gradient-vs-Chl scatter points for the graph tool, using the nearest 8d (falling back to 1d) Chl file
 * for each point's own date, and skipping points with no Chl grid cell within 1km.
 * @param {Array<Object>} rows Selected CO2 rows with latitude/longitude/_date and the gradient field populated.
 * @param {string} gradientField Row property name holding the gradient value to plot on the x axis.
 * @returns {Promise<{points: Array<{x:number,y:number}>, skippedCount: number}>} Plotted points and skip count.
 */
async function computeGradientChlPoints(rows, gradientField) {
  const points = [];
  let skippedCount = 0;
  const manifestText = await ensureChlManifestText(show_location);
  if (!manifestText) {
    return {points, skippedCount: rows.length};
  }

  for (const row of rows) {
    const gradientValue = toFiniteNumber(row[gradientField]);
    const latitude = toFiniteNumber(row.latitude);
    const longitude = toFiniteNumber(row.longitude);
    const dateValue = row._date instanceof Date ? row._date : new Date(row._date);
    if (gradientValue === null || latitude === null || longitude === null || Number.isNaN(dateValue?.getTime?.())) {
      skippedCount += 1;
      continue;
    }

    const selectedFile = selectNearestChlFile(manifestText, dateValue);
    if (!selectedFile) {
      skippedCount += 1;
      continue;
    }

    const chlRows = await loadChlFileRows(show_location, selectedFile);
    let nearestChl = null;
    let nearestDistanceKm = Infinity;
    chlRows.forEach((chlRow) => {
      const distanceKm = haversineDistanceKm(latitude, longitude, chlRow.latitude, chlRow.longitude);
      if (distanceKm < nearestDistanceKm) {
        nearestDistanceKm = distanceKm;
        nearestChl = chlRow.chl;
      }
    });

    if (nearestChl === null || nearestDistanceKm > 100) {
      skippedCount += 1;
      continue;
    }
    points.push({x: gradientValue, y: nearestChl});
  }

  return {points, skippedCount};
}

// Averages every Chl grid cell inside a lat/lon bounding box, for every entry of one Chl file period.
async function computeChlAveragesForEntries(entries, bounds) {
  const points = [];
  for (const entry of entries) {
    const chlRows = await loadChlFileRows(show_location, entry);
    if (!chlRows || chlRows.length < 8) continue;  // not enough data for this date
    const valuesInBounds = chlRows
      .filter((row) => row.latitude >= bounds.minLat && row.latitude <= bounds.maxLat &&
        row.longitude >= bounds.minLon && row.longitude <= bounds.maxLon && row.chl < 10.0)
      .map((row) => row.chl);
    if (!valuesInBounds.length) continue;
    const average = valuesInBounds.reduce((sum, value) => sum + value, 0) / valuesInBounds.length;
    points.push({x: entry.date.getTime(), y: average});
  }
  return points;
}

/**
 * Build "Chlorophyll over time" line graph points by averaging every 1d and 8d Chl grid cell inside a
 * lat/lon bounding box, for every 1d/8d Chl file listed in chl_days.txt.
 * @param {{minLat:number,maxLat:number,minLon:number,maxLon:number}} bounds Lat/lon bounding box to average within.
 * @returns {Promise<{points1d: Array<{x:number,y:number}>, points8d: Array<{x:number,y:number}>}>} One point (file date, averaged Chl) per file that has data inside the box, for each period.
 */
async function computeChlOverTimePoints(bounds) {
  const manifestText = await ensureChlManifestText(show_location);
  if (!manifestText) return {points1d: [], points8d: []};

  // Set cursor to busy
  document.body.style.cursor = 'wait';
  const points1d = await computeChlAveragesForEntries(listChlEntries(manifestText, '1d'), bounds);
  const points8d = await computeChlAveragesForEntries(listChlEntries(manifestText, '8d'), bounds);
  document.body.style.cursor = 'default'; // Reset cursor to default
  return {points1d, points8d};
}

function toFiniteNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const MIN_VALLEY_WIDTH = 40 // Minimum width of a valley to be considered worth looking at

function getValleyBounds(row) {
  const centerIndex = toFiniteNumber(row?._index);
  const leftOffset = toFiniteNumber(row?.valley_left_ips);
  const rightOffset = toFiniteNumber(row?.valley_right_ips);
  const valleyWidth = toFiniteNumber(row?.valley_width);
  if (row?.valleys !== '1' || centerIndex === null || valleyWidth === null || valleyWidth < MIN_VALLEY_WIDTH
    || leftOffset === null || rightOffset === null || leftOffset >= 0 || rightOffset <= 0) {
    // if (row?.valleys === "1") console.log('Invalid valley row:', row);
    return null;
  }
  return {
    leftIndex: Math.ceil(centerIndex + leftOffset),
    rightIndex: Math.floor(centerIndex + rightOffset)
  };
}

function getDisplayMetric(showValue) {
  const configMap = {
    raw: { field: 'xco2', unit: 'ppm', decimals: 1, scale: scale_type.co2_reading, midpoint: 'mean' },
    raw_avg12: { field: 'xco2_avg_12', unit: 'ppm', decimals: 1, scale: scale_type.co2_reading, midpoint: 'mean' },
    raw_avg25: { field: 'xco2_avg_25', unit: 'ppm', decimals: 1, scale: scale_type.co2_reading, midpoint: 'mean' },
    co2_max_min: { field: 'xco2_avg_25', unit: 'ppm', decimals: 1, scale: scale_type.co2_reading, midpoint: 'mean' },
    delta: { field: 'co2_derivative', unit: 'ppm/hr', decimals: 2, scale: scale_type.co2_delta, midpoint: 'zero' },
    delta_avg12: { field: 'gradient_avg_12', unit: 'ppm/hr', decimals: 2, scale: scale_type.co2_delta, midpoint: 'zero' },
    delta_avg25: { field: 'gradient_avg_25', unit: 'ppm/hr', decimals: 2, scale: scale_type.co2_delta, midpoint: 'zero' },
    delta_avg_25_max_min: { field: 'gradient_avg_25', unit: 'ppm/hr', decimals: 2, scale: scale_type.co2_delta, midpoint: 'zero' }
  };

  return configMap[showValue] || configMap.raw;
}

function getRowMetricValue(row, showValue) {
  const metric = getDisplayMetric(showValue);
  return toFiniteNumber(row[metric.field]);
}

function computeMetricRange(rows, showValue) {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  rows.forEach(row => {
    const value = getRowMetricValue(row, showValue);
    if (value === null) return;
    min = Math.min(min, value);
    max = Math.max(max, value);
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return { min, max, ...getDisplayMetric(showValue) };
}

function formatMetricValue(value, decimals, unit) {
  if (!Number.isFinite(value)) return 'N/A';
  return `${value.toFixed(decimals)} ${unit}`;
}

const CO2_VALUE_FILTER_SHOW_TYPES = new Set(['raw', 'raw_avg12', 'raw_avg25', 'co2_max_min']);
const GRADIENT_VALUE_FILTER_SHOW_TYPES = new Set(['delta', 'delta_avg12', 'delta_avg25', 'delta_avg_25_max_min']);

function getValueFilterGroup(showValue) {
  if (GRADIENT_VALUE_FILTER_SHOW_TYPES.has(showValue)) {
    return 'gradient';
  }
  if (CO2_VALUE_FILTER_SHOW_TYPES.has(showValue)) {
    return 'co2';
  }
  return 'co2';
}

function getDefaultValueFilterRangeForGroup(metricRange, filterGroup) {
  if (!metricRange) {
    return null;
  }
  return {min: metricRange.min, max: metricRange.max};
}

const scale_type ={co2_reading: 'co2_reading', co2_delta: 'co2_delta'};

// Color scale function based on co2_derivative
function getColorForValue(value, scale, minValue, maxValue, split = false) {
  value = parseFloat(value);
  if (split) {
    // Handle split scale logic
    if (scale === scale_type.co2_delta || scale === scale_type.co2_delta_avg ) {
      // scale color so negative values map to 0-10 and positive values map to 10-20
      if (minValue < 0 && maxValue > 0) {
        if (value < 0) {
          value = (value - minValue) / (0 - minValue) * 10; // Scale negative values to 0-10 range
        } else {
          value = 10 + (value / maxValue) * 10; // Scale positive values to 10-20 range
        }
      } else if (maxValue <= 0) {
        // All values are negative, scale to 0-20 range
        value = (value - minValue) / (maxValue - minValue) * 20;
      } else {
        // All values are positive, scale to 0-20 range
        value = (value / maxValue) * 20;
      }
      // return `hsl(${240 - 12*value}, 100%, 50%)`; // Map to a hue from blue (240) to red (0)

    } else {
      // 0-40   40-80   80-120   180-200  200-220  220-240
      // 0-3.3 3.3-6.6  6.6-10  10-13.3  13.3-16.6 16.6-20
      // put color in minValue to maxValue range
      if (minValue !== maxValue) {
        value = (value - minValue) / (maxValue - minValue) * 20; // Scale to 0-20 range for color mapping
      } else {
        value = 10; // If all values are the same, use middle of the scale
      }
    }
    let h_value = 120; // Default to blue for missing values
    let l_value = 50; // Default lightness
    if (value <= 10) {
      h_value = 240;
      l_value = 25 + (95-25)*(value/10);
    } else if (value > 10) {
      h_value = 10;
      l_value = 20 + (90-20)*((20-value)/10);
    }
    return `hsl(${h_value}, 100%, ${l_value}%)`; // Map to a hue from blue (240) to red (0)

  } else {
    // Continuous scale logic: rainbow from blue (low) to red (high)
    if (minValue !== maxValue) {
      value = (value - minValue) / (maxValue - minValue) * 300; // Scale to 0-300 range for hue mapping
    } else {
      value = 150; // Default to blue for all same values
    }
    return `hsl(${300 - value}, 100%, 50%)`; // Map to a hue from blue (300) to red (0)
  }
}

// Group data by date section (first 8 chars of sounding_id) and find southernmost point in each
function getOrbitLabelPoints(data) {
  const sections = {};
  
  // Group data by date section
  // This might be incorrect if there are multiple orbits in the data set on the same day.
  data.forEach(row => {
    if (row.sounding_id && row.latitude && row.longitude) {
      const dateSection = row.sounding_id.substring(0, 8); // YYYYMMDD
      if (!sections[dateSection]) {
        sections[dateSection] = [];
      }
      sections[dateSection].push(row);
    }
  });
  
  // For each section, find the southernmost point (minimum latitude)
  const labelPoints = [];
  Object.keys(sections).sort().forEach(dateSection => {
    const sectionData = sections[dateSection];
    let southernmost = sectionData[0];
    let minLat = parseFloat(southernmost.latitude);
    
    for (let i = 1; i < sectionData.length; i++) {
      const lat = parseFloat(sectionData[i].latitude);
      if (lat < minLat) {
        minLat = lat;
        southernmost = sectionData[i];
      }
    }
    
    // Format date as YYYY-MM-DD
    const year = dateSection.substring(0, 4);
    const month = dateSection.substring(4, 6);
    const day = dateSection.substring(6, 8);
    const formattedDate = `${year}-${month}-${day}`;
    
    labelPoints.push({
      date: formattedDate,
      lon: parseFloat(southernmost.longitude),
      lat: parseFloat(southernmost.latitude)
    });
  });
  
  return labelPoints;
}

// Create label features with text and connecting lines
function createLabelFeatures(labelPoints) {
  const features = [];
  
  labelPoints.forEach(point => {
    // Create line from point to label location (below)
    const lineFeature = new Feature({
      geometry: new LineString([
        fromLonLat([point.lon, point.lat]),
        fromLonLat([point.lon, point.lat - 0.3]) // Line goes down
      ]),
      isOrbitLabel: true
    });
    
    lineFeature.setStyle(new Style({
      stroke: new Stroke({
        color: 'rgba(100, 100, 100, 0.7)',
        width: 1
      })
    }));
    
    features.push(lineFeature);
    
    // Create text label feature
    const labelFeature = new Feature({
      geometry: new Point(fromLonLat([point.lon, point.lat - 0.3])),
      text: point.date,
      isOrbitLabel: true
    });
    
    labelFeature.setStyle(new Style({
      text: new Text({
        text: point.date,
        fontSize: '12px',
        fontFamily: 'Arial',
        fill: new Fill({color: 'rgba(0, 0, 0, 1)'}),
        stroke: new Stroke({color: 'rgba(255, 255, 255, 0.8)', width: 3}),
        padding: [2, 4, 2, 4],
        textAlign: 'center',
        offsetY: 5
      }),
      image: new Circle({
        radius: 0, // No circle, just text
        fill: new Fill({color: 'rgba(255, 255, 255, 0.9)'}),
        stroke: new Stroke({color: 'rgba(100, 100, 100, 0.5)', width: 1})
      })
    }));
    
    features.push(labelFeature);
  });
  
  return features;
}

// Create popup overlay
const createPopupOverlay = () => {
  const popupContainer = document.createElement('div');
  popupContainer.id = 'popup';
  popupContainer.style.position = 'absolute';
  popupContainer.style.backgroundColor = 'white';
  popupContainer.style.padding = '8px';
  popupContainer.style.borderRadius = '4px';
  popupContainer.style.border = '2px solid #333';
  popupContainer.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
  popupContainer.style.fontSize = '12px';
  popupContainer.style.zIndex = '1000';
  popupContainer.style.maxWidth = '260px';
  popupContainer.style.width = '160px';
  popupContainer.style.display = 'none';
  popupContainer.style.userSelect = 'text';
  popupContainer.style.webkitUserSelect = 'text';
  document.body.appendChild(popupContainer);
  
  // Prevent map interactions when interacting with popup
  popupContainer.addEventListener('mousedown', (e) => {
    e.stopPropagation();
  });
  popupContainer.addEventListener('touchstart', (e) => {
    e.stopPropagation();
  });
  
  return {
    container: popupContainer,
    overlay: new Overlay({
      element: popupContainer,
      positioning: 'bottom-center',
      offset: [15, -15],
      stopEvent: true
    })
  };
};

// Map of show dropdown labels to file tags
const showTagMap = {
  'Raw': 'raw',
  'Avg 12km': 'raw_avg12',
  'Avg 25km': 'raw_avg25',
  'Gradient': 'delta',
  'Gradient Avg 12km': 'delta_avg12',
  'Gradient Avg 25km': 'delta_avg25',
  'CO2 Max/Min': 'co2_max_min',
  'Gradient Avg 25km Max/Min': 'delta_avg_25_max_min'
};

const URL_BOOLEAN_PARAMS = {
  wind_arrows: 'show_wind_arrows',
  grid_wind_arrows: 'show_grid_wind_arrows',
  orbit_labels: 'show_orbit_labels',
  split_spectrum: 'show_split_color_spectrum',
  exclude: 'exclude_edges',
  land: 'show_land_points',
  clouds: 'show_clouds',
  iffy_points: 'mark_iffy_points',
  current_arrows: 'show_current_arrows',
  oco2: 'show_oco2',
  oco3: 'show_oco3',
  socat: 'show_socat',
  chl: 'show_chl',
  vorticity: 'show_vorticity',
  bga: 'show_bga',
  mblh: 'show_mblh',
  peaks: 'show_peaks',
  valleys: 'show_valleys',
  iffy_filter: 'use_iffy_filter_files'
};

const URL_BOOLEAN_DEFAULTS = {
  wind_arrows: false,
  grid_wind_arrows: false,
  orbit_labels: true,
  split_spectrum: false,
  exclude: true,
  land: false,
  clouds: false,
  iffy_points: false,
  current_arrows: false,
  oco2: true,
  oco3: true,
  socat: false,
  chl: false,
  vorticity: false,
  bga: false,
  mblh: false,
  peaks: false,
  valleys: false,
  iffy_filter: false
};

const URL_DEFAULT_SHOW = 'raw_avg25';
const URL_PARAM_MAX_LENGTH = 160;
const URL_MAX_POINTS = 10;

function stripWrappingQuotes(value) {
  if (typeof value !== 'string') return value;
  if (value.length >= 2) {
    const first = value[0];
    const last = value[value.length - 1];
    if ((first === '"' && last === '"') || (first === '\'' && last === '\'')) {
      return value.slice(1, -1);
    }
  }
  return value;
}

function sanitizeQueryRaw(value, maxLength = URL_PARAM_MAX_LENGTH) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return null;
  if (value.length > maxLength) return null;
  return stripWrappingQuotes(value.trim());
}

function parseStrictNumber(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDateYYYYMMDD(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;

  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  const dateValue = new Date(year, month - 1, day);
  if (Number.isNaN(dateValue.getTime())) return null;
  if (
    dateValue.getFullYear() !== year ||
    dateValue.getMonth() !== month - 1 ||
    dateValue.getDate() !== day
  ) {
    return null;
  }

  return dateValue;
}

function parseLatLonPair(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  const pair = trimmed.split(',').map(part => part.trim());
  if (pair.length !== 2) return null;

  const lat = parseStrictNumber(pair[0]);
  const lon = parseStrictNumber(pair[1]);
  if (lat === null || lon === null) return null;
  if (lat < -90 || lat > 90) return null;
  if (lon < -180 || lon > 180) return null;
  return { lat, lon };
}

function parseBooleanLikeParam(rawValue) {
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim().toLowerCase();
  if (trimmed === '' || trimmed === '0' || trimmed === 'false') return false;
  if (trimmed === '1' || trimmed === 'true') return true;
  return null;
}

function parseStartupUrlQuery() {
  const params = new URLSearchParams(window.location.search);
  const ignored = new Set();
  const hasAnyParams = Array.from(params.keys()).length > 0;
  const chlFolderParam = sanitizeQueryRaw(params.get('chlsrc'))?.toLowerCase();
  if (chlFolderParam === 'occci') chl_folder = 'occci';
  if (chlFolderParam === 'chl') chl_folder = 'chl';
  const parsed = {
    hasAnyParams,
    ignoredParams: [],
    checkboxes: {},
    show: null,
    location: null,
    fromDate: null,
    toDate: null,
    fromDateRequested: params.has('from_date'),
    toDateRequested: params.has('to_date'),
    valueLow: null,
    valueHigh: null,
    valueLowParamName: null,
    valueHighParamName: null,
    valueLowRequested: params.has('value_filter_low') || params.has('value_low'),
    valueHighRequested: params.has('value_filter_high') || params.has('value_high'),
    points: [],
    lines: [],
    center: null,
    zoom: null,
    centerRequested: params.has('center'),
    zoomRequested: params.has('zoom')
  };

  Object.keys(URL_BOOLEAN_PARAMS).forEach(paramName => {
    if (!params.has(paramName)) return;
    const sanitized = sanitizeQueryRaw(params.get(paramName));
    if (sanitized === null) {
      ignored.add(paramName);
      return;
    }
    const parsedValue = parseBooleanLikeParam(sanitized);
    if (parsedValue === null) {
      ignored.add(paramName);
      return;
    }
    parsed.checkboxes[paramName] = parsedValue;
  });

  if (params.has('mblht')) {
    mblh_time = normalizeMblhTime(sanitizeQueryRaw(params.get('mblht')));
  }

  if (params.has('show')) {
    const sanitized = sanitizeQueryRaw(params.get('show'));
    if (!sanitized) {
      ignored.add('show');
    } else {
      parsed.show = sanitized;
    }
  }

  if (params.has('location')) {
    const sanitized = sanitizeQueryRaw(params.get('location'));
    if (!sanitized) {
      ignored.add('location');
    } else {
      parsed.location = sanitized;
    }
  }

  if (params.has('from_date')) {
    const sanitized = sanitizeQueryRaw(params.get('from_date'));
    const parsedDate = parseDateYYYYMMDD(sanitized);
    if (!parsedDate) {
      ignored.add('from_date');
    } else {
      parsed.fromDate = parsedDate;
    }
  }

  if (params.has('to_date')) {
    const sanitized = sanitizeQueryRaw(params.get('to_date'));
    const parsedDate = parseDateYYYYMMDD(sanitized);
    if (!parsedDate) {
      ignored.add('to_date');
    } else {
      parsed.toDate = parsedDate;
    }
  }

  const valueLowName = params.has('value_filter_low') ? 'value_filter_low' : (params.has('value_low') ? 'value_low' : null);
  if (valueLowName) {
    parsed.valueLowParamName = valueLowName;
    const sanitized = sanitizeQueryRaw(params.get(valueLowName));
    const parsedNumber = parseStrictNumber(sanitized || '');
    if (parsedNumber === null) {
      ignored.add(valueLowName);
    } else {
      parsed.valueLow = parsedNumber;
    }
  }

  const valueHighName = params.has('value_filter_high') ? 'value_filter_high' : (params.has('value_high') ? 'value_high' : null);
  if (valueHighName) {
    parsed.valueHighParamName = valueHighName;
    const sanitized = sanitizeQueryRaw(params.get(valueHighName));
    const parsedNumber = parseStrictNumber(sanitized || '');
    if (parsedNumber === null) {
      ignored.add(valueHighName);
    } else {
      parsed.valueHigh = parsedNumber;
    }
  }

  for (let i = 1; i <= URL_MAX_POINTS; i++) {
    const pointParamName = `point${i}`;
    if (!params.has(pointParamName)) continue;
    const sanitized = sanitizeQueryRaw(params.get(pointParamName));
    const point = parseLatLonPair(sanitized || '');
    if (!point) {
      ignored.add(pointParamName);
      continue;
    }
    parsed.points.push(point);
  }

  for (let i = 1; i <= MEASURE_MAX_LINES; i++) {
    const lineParamName = `line${i}`;
    if (!params.has(lineParamName)) continue;
    const sanitized = sanitizeQueryRaw(params.get(lineParamName), URL_PARAM_MAX_LENGTH * 2);
    const lineDef = parseMeasureLineQueryValue(sanitized || '');
    if (!lineDef) {
      ignored.add(lineParamName);
      continue;
    }
    parsed.lines.push(lineDef);
  }

  if (params.has('center')) {
    const sanitized = sanitizeQueryRaw(params.get('center'));
    const center = parseLatLonPair(sanitized || '');
    if (!center) {
      ignored.add('center');
    } else {
      parsed.center = center;
    }
  }

  if (params.has('zoom')) {
    const sanitized = sanitizeQueryRaw(params.get('zoom'));
    const zoom = parseStrictNumber(sanitized || '');
    if (zoom === null || zoom < 0 || zoom > 30) {
      ignored.add('zoom');
    } else {
      parsed.zoom = zoom;
    }
  }

  parsed.ignoredParams = Array.from(ignored);
  return parsed;
}


// Alternative: scan for files by trying to load them
async function scanForDataFiles() {
  
  // Read location names from data/locations.txt file
  let commonLocations = [];
  try {
    const response = await fetch('./data/locations.txt');
    const text = await response.text();
    commonLocations = text.split('\n').map(line => line.trim()).filter(line => line.length > 0 && !line.startsWith('//#'));
  } catch (e) {
    console.warn ('Could not read data/locations.txt file, using empty list:', e);
    updateMessage ('Warning: Could not read data/locations.txt file, using empty location list');
  }
  show_location = commonLocations.length > 0 ? commonLocations[0] : "No locations found"; // Set default location to first in list if available
  
  return  commonLocations;

}

// SLA icon configuration - easy to modify ranges and file references
const SLA_ICON_CONFIG = [
  { min: 0.3, max: Infinity, file: 'SLA_UP_3.gif' },
  { min: 0.2, max: 0.3, file: 'SLA_UP_2.gif' },
  { min: 0.1, max: 0.2, file: 'SLA_UP_1.gif' },
  { min: -0.2, max: -0.1, file: 'SLA_DOWN_1.gif' },
  { min: -0.3, max: -0.2, file: 'SLA_DOWN_2.gif' },
  { min: -Infinity, max: -0.3, file: 'SLA_DOWN_3.gif' }
];

// Helper function to get SLA icon file based on value
function getSLAIconFile(slaValue) {
  if (!slaValue || slaValue === '') return null;
  
  const value = parseFloat(slaValue);
  if (isNaN(value) || Math.abs(value) < 0.1) return null;
  
  for (const range of SLA_ICON_CONFIG) {
    if (value >= range.min && value < range.max) {
      return range.file;
    }
  }
  
  return null;
}

let map_layer; // Keep reference to map layer for data loading
let api_key = null; // Store API key for Google Maps
let google_map_roadmap_source = null; // Store Google Maps roadmap layer
let google_map_satellite_source = null; // Store Google Maps satellite layer
let carto_map_source = null; // Store CartoDB layer for fallback
let background_tile_layer = null; // Store current background tile layer
let vorticityHeatmapLayer = null; // Store vorticity point overlay layer
let vorticityFeatureSource = null; // Source used for vorticity hover fallback when heatmap pixels are faint.
let vorticitySpectrumArea = null; // Store vorticity spectrum display
let chlSpectrumArea = null; // Store CHL spectrum display
let bgaSpectrumArea = null; // Store BGA spectrum display
let mblhSpectrumArea = null; // Store MBLH spectrum display
let bgaLayer = null; // Store Blue-Green Algae overlay layer
let bgaFeatureSource = null; // Store BGA point features for hover lookup
let popupVorticityPointSource = null; // Cached vorticity points for popup supplemental values
let popupVorticityDate = null; // Date for popup vorticity cache (YYYY-MM-DD)
let popupChlPointSource = null; // Cached CHL points for popup supplemental values
let popupChlDate = null; // Date for popup CHL cache (YYYY-MM-DD)
  
// class GoogleLogoControl extends Control {
//     constructor() {
//       const element = document.createElement('img');
//       element.style.pointerEvents = 'none';
//       element.style.position = 'absolute';
//       element.style.bottom = '5px';
//       element.style.left = '5px';
//       element.src =
//         'https://developers.google.com/static/maps/documentation/images/google_on_white.png';
//       super({
//         element: element,
//       });
//     }
// }

function create_google_satellite_source (key) {
  return new Google({
          key,
          scale: 'scaleFactor2x',
          highDpi: true,
          mapType: 'satellite',
          layerTypes: ['layerRoadmap'],
          });
}

function create_google_roadmap_source (key) {
    return new Google({
          key,
          scale: 'scaleFactor2x',
          highDpi: true,
          mapType: 'roadmap',
          layerTypes: ['layerRoadmap'],
        });
}

// Show the map when the user clicks the button.
function showMap(key, startupQueryConfig = null) {
  api_key = key;
  background_tile_layer = new TileLayer();
  if (!api_key || api_key.trim() === "") {
    // Use cartocdn tiles as fallback if no API key provided
    carto_map_source =    new XYZ({
          url: 'https://{a-c}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_27f6_1_4515c2540261181ac50308d3',
          crossOrigin: 'anonymous'
        });
      background_tile_layer.setSource(carto_map_source);
      
    } else {
      if (show_satellite_view) {
        if (!google_map_satellite_source) {
          google_map_satellite_source =  create_google_satellite_source (key)
        };
        background_tile_layer.setSource(google_map_satellite_source);
      } else {
        if (!google_map_roadmap_source) { 
          google_map_roadmap_source = create_google_roadmap_source(key);
        }
        background_tile_layer.setSource(google_map_roadmap_source);
      }
    }



  const popup = createPopupOverlay();
  const popupContainer = popup.container;
  const overlay = popup.overlay;
  
  // Create spectrum area in lower right corner
  const spectrumArea = document.createElement('div');
  spectrumArea.id = 'color-spectrum';
  spectrumArea.style.position = 'fixed';
  spectrumArea.style.right = '100px';
  spectrumArea.style.bottom = '210px';
  spectrumArea.style.padding = '5px 10px';
  spectrumArea.style.minWidth = '320px';
  spectrumArea.style.backgroundColor = '#f0f0f0';
  spectrumArea.style.borderRadius = '4px';
  spectrumArea.style.border = '1px solid #ccc';
  spectrumArea.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
  spectrumArea.style.zIndex = '99';
  spectrumArea.style.fontFamily = 'Arial, sans-serif';
  spectrumArea.style.fontSize = '12px';
  document.body.appendChild(spectrumArea);
  
  const vectorSource = new VectorSource();
  const currentSource = new VectorSource();
  const labelSource = new VectorSource();
  const cloudTileGrid = new WMTSTileGrid({
    origin: [-20037508.342789244, 20037508.342789244],
    resolutions: createXYZ({maxZoom: 9}).getResolutions(),
    matrixIds: Array.from({length: 10}, (_, index) => String(index)),
  });

  const createCloudSource = (dateValue) => {
    const cloudDate = dateValue instanceof Date && !Number.isNaN(dateValue.getTime())
      ? `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`
      : '2024-01-01';

    return new WMTS({
      url: `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_SNPP_CorrectedReflectance_TrueColor/default/${cloudDate}/GoogleMapsCompatible_Level9/{TileMatrix}/{TileRow}/{TileCol}.jpg`,
      layer: 'VIIRS_SNPP_CorrectedReflectance_TrueColor',
      matrixSet: 'GoogleMapsCompatible_Level9',
      format: 'image/jpeg',
      projection: 'EPSG:3857',
      tileGrid: cloudTileGrid,
      style: 'default',
      crossOrigin: 'anonymous',
      wrapX: true,
      requestEncoding: 'REST',
    });
  };

  const cloudLayer = new TileLayer({
    visible: show_clouds,
    opacity: 0.85,
    source: createCloudSource(date_range_start || date_range_min || new Date('2024-01-01')),
  });

  const updateCloudLayerDate = (dateValue) => {
    if (!dateValue || !(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return;
    if (!show_clouds) return;
    // Compare by time value, not object reference, so re-enabling clouds for the same date still shows them.
    if (!last_shown_cloud_date || last_shown_cloud_date.getTime() !== dateValue.getTime()) {
      const nextSource = createCloudSource(dateValue);
      cloudLayer.setSource(nextSource);
      last_shown_cloud_date = dateValue;
    }
    cloudLayer.setVisible(true);
  };



  
  const controlBand = createControlBand(api_key, showTagMap, show_selection, show_full_orbits, show_chl, show_sla, 
                                        show_wind_arrows, show_satellite_view, show_orbit_labels, 
                                        show_split_color_spectrum, exclude_edges, show_clouds,
                                        mark_iffy_points, show_current_arrows, show_land_points, show_oco2, show_oco3, show_vorticity, show_grid_wind_arrows, show_socat, show_bga, show_peaks, show_valleys, use_iffy_filter_files, show_mblh);

  const updateSpectrumSummary = () => {
    const spectrumContainer = document.getElementById('color-spectrum');
    if (!spectrumContainer) return;

    let summaryBox = document.getElementById('color-spectrum-summary');
    if (!summaryBox) {
      summaryBox = document.createElement('div');
      summaryBox.id = 'color-spectrum-summary';
      summaryBox.style.marginTop = '6px';
      summaryBox.style.padding = '4px 6px';
      summaryBox.style.backgroundColor = '#ffffff';
      summaryBox.style.border = '1px solid #ccc';
      summaryBox.style.borderRadius = '3px';
      summaryBox.style.fontSize = '10px';
      summaryBox.style.lineHeight = '1.3';
      summaryBox.style.color = '#333';
      summaryBox.style.whiteSpace = 'normal';
      summaryBox.style.wordBreak = 'break-word';
      summaryBox.style.textAlign = 'center';
      spectrumContainer.appendChild(summaryBox);
    }

    const showLabel = controlBand.showDropdown.options[controlBand.showDropdown.selectedIndex]?.textContent || show_selection;
    const locationLabel = controlBand.locationDropdown.options[controlBand.locationDropdown.selectedIndex]?.textContent || show_location;
    const rangeText = date_range_start && date_range_end
      ? `${date_range_start.toISOString().slice(0, 10)} → ${date_range_end.toISOString().slice(0, 10)}`
      : 'No range';
    const excludeSuffix = exclude_edges ? ' X' : '';
    // Single combined line for the four date-driven overlays, using the one shared feature_display_date.
    const anyFeatureActive = show_clouds || show_current_arrows || show_vorticity || show_grid_wind_arrows || show_bga || show_mblh;
    const featureDateText = feature_display_date ? formatDateAsYYYYMMDDWithHyphens(feature_display_date) : null;
    const featureSuffix = anyFeatureActive && featureDateText
      ? `Clouds, Currents, Vorticity, BGA, MBLH, and Grid Wind as of ${featureDateText}`
      : '';
    const footerParts = [featureSuffix].filter(Boolean);
    const breakText = footerParts.length > 0 ? '<br>' : '';
    summaryBox.innerHTML = `${showLabel} | ${locationLabel} | ${rangeText}${excludeSuffix}${breakText}${footerParts.join(' | ')}`;
  };
  const mousePositionControl = new MousePosition({
  coordinateFormat: createStringXY(4), // 4 decimal places
  projection: 'EPSG:4326', // Use Lat/Lon
  target: document.getElementById('mouse-position-div'), // Optional external container
  undefinedHTML: '&nbsp;'
});

  const dataLayer = new VectorLayer({     // data overlay from CSV
    source: vectorSource,
    declutter: false, // Disable decluttering to prevent overlap of features
    zIndex: 150
  });
  
  const labelLayer = new VectorLayer({     // orbit labels layer
    source: labelSource,
    declutter: false,
    visible: show_orbit_labels,
    zIndex: 200
  });

  const currentLayer = new VectorLayer({     // current arrows layer
    source: currentSource,
    declutter: false,
    visible: false, // Visibility is controlled by show_current_arrows and available features.
    zIndex: 125
  });

  const updateCurrentArrowLayerVisibility = () => {
    // Keep the unified current-arrow layer synchronized with checkbox state and available features.
    const hasVorticityCurrentFeatures = currentSource.getFeatures().length > 0;

    if (!show_current_arrows) {
      currentLayer.setVisible(false);
      return;
    }
    currentLayer.setVisible(hasVorticityCurrentFeatures);
  };

  const windAreaSource = new VectorSource();
  const windAreaLayer = new VectorLayer({     // area-wide wind arrows layer, loaded from wind_data.csv
    source: windAreaSource,
    declutter: false,
    visible: false,
    zIndex: 126
  });
  // Cache of parsed wind_data.csv rows, keyed by location, so the file is only fetched once per location.
  let windAreaDataCache = {location: null, rows: null};

  const chlSource = new VectorSource();
  const chlLayer = new VectorLayer({
    source: chlSource,
    declutter: false,
    visible: false,
    opacity: 0.9,
    // Render CHL above data circles so CHL symbol shape is actually visible.
    zIndex: 120
  });
  let chlResolutionListenerKey = null;

  const bgaSource = new VectorSource();
  bgaLayer = new VectorLayer({
    source: bgaSource,
    declutter: false,
    visible: false,
    opacity: 0.9,
    zIndex: 121
  });
  let bgaResolutionListenerKey = null;

  // MBLH cells are real 0.25 degree polygons, so they scale with zoom and latitude automatically.
  const mblhSource = new VectorSource();
  const mblhLayer = new VectorLayer({
    source: mblhSource,
    declutter: false,
    visible: false,
    opacity: 0.9,
    zIndex: 122
  });

  const clickedPointsSource = new VectorSource();
  const clickedPointsLayer = new VectorLayer({     // user-clicked points with labels
    source: clickedPointsSource,
    declutter: false,
    zIndex: 200
  });

  let measureTool = null;
  let graphTool = null;

  const map = new Map({
    layers: [background_tile_layer,  // base map from Google or elsewhere
             cloudLayer,
             chlLayer,
             bgaLayer,
             mblhLayer,
             currentLayer,
             windAreaLayer,
             dataLayer,
             labelLayer,
             clickedPointsLayer,
             new Graticule({
                // Line style
                strokeStyle: new Stroke({
                  color: 'rgba(255, 120, 0, 0.5)',
                  width: 2,
                  lineDash: [0.2, 8],
                }),
                showLabels: true, // Show latitude/longitude labels
                wrapX: true,
                zIndex: 350
          })
    ],
    // controls: defaultControls().extend([new GoogleLogoControl()]),
    target: 'map',
    overlays: [overlay],
    view: new View({
      center: [0, 0],
      zoom: 2,
    }),
  });

  // Add some attribution html in the lower corner of the map.
  const attributionElement = document.createElement('div');
  attributionElement.className = 'map-attribution ol-unselectable ol-control';
  attributionElement.innerHTML = '<a href="http://www.openstreetmap.org/copyright" target="_blank" rel="noopener">&copy; OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>';
  map.addControl(new Control({ element: attributionElement }));

  // Add the CarbonViews.org brand in a fixed screen position above the map.
  const brandElement = document.createElement('div');
  brandElement.className = 'map-brand ol-unselectable ol-control';
  brandElement.innerHTML = '<img src="./logo.png" alt="CarbonViews.org logo"><span>CarbonViews.org</span><object type="text/html" data="version.html" height="18" width="80"></object>';
  map.addControl(new Control({ element: brandElement }));

  map.addControl(mousePositionControl);

  // Initialize the measure tool layer and callbacks after map creation.
  measureTool = createMeasureToolManager({
    map,
    onStatus: (statusText) => {
      updateMessage(statusText);
    },
    onLinesChanged: () => {
      syncUrlWithCurrentState();
    },
    onModeChange: ({isActive}) => {
      if (!controlBand.measureToolButton) return;
      controlBand.measureToolButton.textContent = isActive ? 'Cancel Measure' : 'Measure Tool';
      controlBand.measureToolButton.classList.toggle('active', isActive);
    }
  });
  map.addLayer(measureTool.layer);

  graphTool = new GraphTool({
    map,
    button: controlBand.graphToolButton,
    getDisplayedFeatures: () => vectorSource.getFeatures().filter((feature) => feature.get('isGraphDataPoint') === true),
    chlEnabled: () => show_chl,
    getChlValue: (latitude, longitude) => {
      if (!show_chl || !chlSource) return null;
      const lat = toFiniteNumber(latitude);
      const lon = toFiniteNumber(longitude);
      if (lat === null || lon === null) return null;
      const nearest = chlSource.getClosestFeatureToCoordinate(fromLonLat([lon, lat]));
      return toFiniteNumber(nearest?.get('chlValue'));
    },
    getIffyPointSymbol: (row) => getCloudMarkSymbol(row, show_selection),
    getValleyBounds,
    valleysEnabled: () => show_valleys,
    getGraphSummary: (include_dates = true)  => {
      const showLabel = controlBand.showDropdown.options[controlBand.showDropdown.selectedIndex]?.textContent || show_selection;
      const locationLabel = controlBand.locationDropdown.options[controlBand.locationDropdown.selectedIndex]?.textContent || show_location;
      const rangeText = date_range_start && date_range_end
        ? `${date_range_start.toISOString().slice(0, 10)} → ${date_range_end.toISOString().slice(0, 10)}`
        : 'No range';
      return `${showLabel} | ${locationLabel} | ${include_dates ? rangeText : ''}`;
    },
    getGradientChlPoints: (rows, gradientField) => computeGradientChlPoints(rows, gradientField),
    getGradientVorticityPoints: (rows, gradientField) => computeGradientVorticityPoints(rows, gradientField),
    getGradientChlVorticityPoints: (rows, gradientField) => computeGradientChlVorticityPoints(rows, gradientField),
    getFullDateRange: () => (date_range_min && date_range_max ? {min: date_range_min, max: date_range_max} : null),
    getChlOverTimePoints: (bounds) => computeChlOverTimePoints(bounds)
  });

  // Function to update message in control band
  const updateMessage = (text) => {
    // Update text to support line breaks
    text = text.replace(/\n/g, '<br>');
    messageText = text;
    controlBand.messageArea.innerHTML = text;
    controlBand.messageArea.scrollTop = controlBand.messageArea.scrollHeight;
  };

  let copyStatusTimeoutId = null;
  const setCopyStatus = (text) => {
    if (!controlBand.copyStatusArea) return;
    if (copyStatusTimeoutId) {
      clearTimeout(copyStatusTimeoutId);
      copyStatusTimeoutId = null;
    }
    controlBand.copyStatusArea.textContent = text;
    if (text) {
      copyStatusTimeoutId = setTimeout(() => {
        controlBand.copyStatusArea.textContent = '';
        copyStatusTimeoutId = null;
      }, 3000);
    }
  };

  const formatLatLon = (lat, lon, decimals = 6) => `${Number(lat).toFixed(decimals)},${Number(lon).toFixed(decimals)}`;
  const startupIgnoredParams = new Set(startupQueryConfig?.ignoredParams || []);
  let suppressUrlSync = false;
  let defaultLocationForUrl = null;

  const copyTextToClipboard = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    // Fallback for non-secure contexts where Clipboard API may be unavailable.
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  const sameDate = (a, b) => {
    if (!(a instanceof Date) || Number.isNaN(a.getTime())) return false;
    if (!(b instanceof Date) || Number.isNaN(b.getTime())) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };

  const formatDateForQuery = (dateValue) => {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return null;
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatNumberForQuery = (value, decimals = 6) => {
    if (!Number.isFinite(value)) return null;
    return Number(value).toFixed(decimals).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  };

  const getCloudMarkSymbol = (row, selection) => {
    if (!mark_iffy_points) return null;

    if (selection === 'raw' ||
      selection === 'raw_avg12' ||
      selection === 'raw_avg25' ||
      selection === 'co2_max_min') {
        const std_field = 'xco2_std_15km';
        const std_Value = toFiniteNumber(row[std_field]);
        if (std_Value === null || std_Value < 0.45) return null;
        if (std_Value > 0.65) return 'X';
        return '?';
    } else if (selection === 'delta_avg12' ||
          selection === 'delta_avg25' ||
          selection === 'delta_avg_25_max_min') {
            // For gradient-based selections, use the gradient standard deviation field
            // Value range is also different.
        const std_field = 'gradient_std_12';
        const std_value = toFiniteNumber(row[std_field]);
        if (std_value === null || std_value < 1.0) return null;
        if (std_value > 1.5) return 'X';
        return '?';
      } else {
        return null;
      }

    if (!stdField) return null;
  };

  const setStartupCheckboxState = (variableName, checkedValue) => {
    const checkboxRef = controlBand.checkboxes.find(cb => cb.variable === variableName);
    if (checkboxRef?.element) {
      checkboxRef.element.checked = checkedValue;
    }

    if (variableName === 'show_chl') show_chl = checkedValue;
    else if (variableName === 'show_wind_arrows') show_wind_arrows = checkedValue;
    else if (variableName === 'show_grid_wind_arrows') show_grid_wind_arrows = checkedValue;
    else if (variableName === 'show_orbit_labels') show_orbit_labels = checkedValue;
    else if (variableName === 'show_split_color_spectrum') show_split_color_spectrum = checkedValue;
    else if (variableName === 'exclude_edges') exclude_edges = checkedValue;
    else if (variableName === 'show_clouds') show_clouds = checkedValue;
    else if (variableName === 'mark_iffy_points') mark_iffy_points = checkedValue;
    else if (variableName === 'show_current_arrows') show_current_arrows = checkedValue;
    else if (variableName === 'show_land_points') show_land_points = checkedValue;
    else if (variableName === 'show_oco2') show_oco2 = checkedValue;
    else if (variableName === 'show_oco3') show_oco3 = checkedValue;
    else if (variableName === 'show_socat') show_socat = checkedValue;
    else if (variableName === 'show_vorticity') show_vorticity = checkedValue;
    else if (variableName === 'show_bga') show_bga = checkedValue;
    else if (variableName === 'show_mblh') show_mblh = checkedValue;
    else if (variableName === 'show_peaks') show_peaks = checkedValue;
    else if (variableName === 'show_valleys') show_valleys = checkedValue;
    else if (variableName === 'use_iffy_filter_files') use_iffy_filter_files = checkedValue;
  };

  const buildBookmarkUrl = () => {
    const nextUrl = new URL(window.location.href);
    const nextParams = new URLSearchParams();

    Object.keys(URL_BOOLEAN_PARAMS).forEach(paramName => {
      const variableName = URL_BOOLEAN_PARAMS[paramName];
      const defaultValue = URL_BOOLEAN_DEFAULTS[paramName];
      const currentValue = (
        variableName === 'show_wind_arrows' ? show_wind_arrows :
        variableName === 'show_grid_wind_arrows' ? show_grid_wind_arrows :
        variableName === 'show_orbit_labels' ? show_orbit_labels :
        variableName === 'show_split_color_spectrum' ? show_split_color_spectrum :
        variableName === 'exclude_edges' ? exclude_edges :
        variableName === 'show_land_points' ? show_land_points :
        variableName === 'show_clouds' ? show_clouds :
        variableName === 'mark_iffy_points' ? mark_iffy_points :
        variableName === 'show_current_arrows' ? show_current_arrows :
        variableName === 'show_oco2' ? show_oco2 :
        variableName === 'show_oco3' ? show_oco3 :
        variableName === 'show_socat' ? show_socat :
        variableName === 'show_chl' ? show_chl :
        variableName === 'show_bga' ? show_bga :
        variableName === 'show_mblh' ? show_mblh :
        variableName === 'show_peaks' ? show_peaks :
        variableName === 'show_valleys' ? show_valleys :
        variableName === 'use_iffy_filter_files' ? use_iffy_filter_files :
        show_vorticity
      );

      if (Boolean(currentValue) !== Boolean(defaultValue)) {
        nextParams.set(paramName, currentValue ? '1' : '0');
      }
    });

    if (show_selection && show_selection !== URL_DEFAULT_SHOW) {
      const showLabel = Object.keys(showTagMap).find(label => showTagMap[label] === show_selection) || show_selection;
      nextParams.set('show', showLabel);
    }

    if (mblh_time !== MBLH_DEFAULT_TIME) {
      nextParams.set('mblht', mblh_time);
    }

    if (show_location && defaultLocationForUrl && show_location !== defaultLocationForUrl) {
      nextParams.set('location', show_location);
    }

    if (date_range_min && date_range_max && date_range_start && date_range_end) {
      const defaultStart = new Date(date_range_min);
      const defaultEnd = addDays(defaultStart, 30) > date_range_max ? new Date(date_range_max) : addDays(defaultStart, 30);

      if (!sameDate(date_range_start, defaultStart)) {
        const fromDateText = formatDateForQuery(date_range_start);
        if (fromDateText) nextParams.set('from_date', fromDateText);
      }
      if (!sameDate(date_range_end, defaultEnd)) {
        const toDateText = formatDateForQuery(date_range_end);
        if (toDateText) nextParams.set('to_date', toDateText);
      }
    }

    if (
      Number.isFinite(value_filter_absolute_min) &&
      Number.isFinite(value_filter_absolute_max) &&
      Number.isFinite(value_filter_min) &&
      Number.isFinite(value_filter_max)
    ) {
      if (Math.abs(value_filter_min - value_filter_absolute_min) > 1e-9) {
        nextParams.set('value_filter_low', formatNumberForQuery(value_filter_min, value_filter_decimals) || String(value_filter_min));
      }
      if (Math.abs(value_filter_max - value_filter_absolute_max) > 1e-9) {
        nextParams.set('value_filter_high', formatNumberForQuery(value_filter_max, value_filter_decimals) || String(value_filter_max));
      }
    }

    clickedPoints.slice(0, URL_MAX_POINTS).forEach((pointFeature, index) => {
      const coords = pointFeature.get('clickedCoords');
      if (!coords) return;
      const lonlat = toLonLat(coords);
      if (!Array.isArray(lonlat) || lonlat.length < 2) return;
      nextParams.set(`point${index + 1}`, formatLatLon(lonlat[1], lonlat[0]));
    });

    if (measureTool) {
      measureTool.getSerializedLines().slice(0, MEASURE_MAX_LINES).forEach((lineDef, index) => {
        const lineText = [
          formatNumberForQuery(lineDef.lat1, 6),
          formatNumberForQuery(lineDef.lon1, 6),
          formatNumberForQuery(lineDef.lat2, 6),
          formatNumberForQuery(lineDef.lon2, 6)
        ].join(',');
        nextParams.set(`line${index + 1}`, lineText);
      });
    }

    const mapCenter = map.getView().getCenter();
    if (Array.isArray(mapCenter) && mapCenter.length >= 2) {
      const centerLonLat = toLonLat(mapCenter);
      const centerLat = centerLonLat[1];
      const centerLon = centerLonLat[0];
      if (Math.abs(centerLat) > 1e-9 || Math.abs(centerLon) > 1e-9) {
        nextParams.set('center', formatLatLon(centerLat, centerLon));
      }
    }

    const currentZoom = map.getView().getZoom();
    if (Number.isFinite(currentZoom) && Math.abs(currentZoom - 2) > 1e-9) {
      nextParams.set('zoom', formatNumberForQuery(currentZoom, 3) || String(currentZoom));
    }

    nextUrl.search = nextParams.toString();
    return nextUrl.toString();
  };

  const syncUrlWithCurrentState = () => {
    if (suppressUrlSync) return;
    const nextUrlText = buildBookmarkUrl();
    window.history.replaceState({}, '', nextUrlText);
  };

  const addIgnoredStartupNote = (paramName) => {
    if (typeof paramName === 'string' && paramName) {
      startupIgnoredParams.add(paramName);
    }
  };

  const applyStartupDateAndValueParams = async () => {
    if (!startupQueryConfig) return;

    let dateRangeChanged = false;
    if ((startupQueryConfig.fromDateRequested || startupQueryConfig.toDateRequested) && date_range_min && date_range_max) {
      let nextStart = date_range_start ? new Date(date_range_start) : new Date(date_range_min);
      let nextEnd = date_range_end ? new Date(date_range_end) : addDays(nextStart, Math.min(30, Math.max(0, diffDays(nextStart, date_range_max))));

      if (startupQueryConfig.fromDateRequested && startupQueryConfig.fromDate) {
        if (startupQueryConfig.fromDate >= date_range_min && startupQueryConfig.fromDate <= date_range_max) {
          nextStart = new Date(startupQueryConfig.fromDate);
          nextEnd = addDays(nextStart, 30) > date_range_max ? new Date(date_range_max) : addDays(nextStart, 30);
          dateRangeChanged = true;
        } else {
          addIgnoredStartupNote('from_date');
        }
      }

      if (startupQueryConfig.toDateRequested && startupQueryConfig.toDate) {
        const toDateLimit = addDays(nextStart, 30);
        const inAbsoluteRange = startupQueryConfig.toDate >= date_range_min && startupQueryConfig.toDate <= date_range_max;
        const withinWindow = startupQueryConfig.toDate >= nextStart && startupQueryConfig.toDate <= toDateLimit;
        if (inAbsoluteRange && withinWindow) {
          nextEnd = new Date(startupQueryConfig.toDate);
          dateRangeChanged = true;
        } else {
          addIgnoredStartupNote('to_date');
        }
      }

      if (nextEnd < nextStart) {
        addIgnoredStartupNote('to_date');
      } else if (dateRangeChanged) {
        date_range_start = nextStart;
        date_range_end = nextEnd;
        date_range_window = Math.max(1, diffDays(date_range_start, date_range_end));
        controlBand.updateDateRangeControls({
          minDate: date_range_min,
          maxDate: date_range_max,
          startDate: date_range_start,
          endDate: date_range_end,
          windowSize: date_range_window,
          disabled: false
        });
        await loadData(show_selection, show_location);
      }
    }

    if (
      (startupQueryConfig.valueLowRequested || startupQueryConfig.valueHighRequested) &&
      Number.isFinite(value_filter_absolute_min) &&
      Number.isFinite(value_filter_absolute_max)
    ) {
      const filterGroup = getValueFilterGroup(show_selection);
      let nextMin = Number.isFinite(value_filter_min) ? value_filter_min : value_filter_absolute_min;
      let nextMax = Number.isFinite(value_filter_max) ? value_filter_max : value_filter_absolute_max;

      if (startupQueryConfig.valueLowRequested && startupQueryConfig.valueLow !== null) {
        if (startupQueryConfig.valueLow < value_filter_absolute_min || startupQueryConfig.valueLow > value_filter_absolute_max) {
          addIgnoredStartupNote(startupQueryConfig.valueLowParamName || 'value_filter_low');
        } else {
          nextMin = startupQueryConfig.valueLow;
        }
      }

      if (startupQueryConfig.valueHighRequested && startupQueryConfig.valueHigh !== null) {
        if (startupQueryConfig.valueHigh < value_filter_absolute_min || startupQueryConfig.valueHigh > value_filter_absolute_max) {
          addIgnoredStartupNote(startupQueryConfig.valueHighParamName || 'value_filter_high');
        } else {
          nextMax = startupQueryConfig.valueHigh;
        }
      }

      if (nextMin > nextMax) {
        if (startupQueryConfig.valueLowRequested) addIgnoredStartupNote(startupQueryConfig.valueLowParamName || 'value_filter_low');
        if (startupQueryConfig.valueHighRequested) addIgnoredStartupNote(startupQueryConfig.valueHighParamName || 'value_filter_high');
      } else {
        const clampedMin = Math.max(value_filter_absolute_min, Math.min(nextMin, value_filter_absolute_max));
        const clampedMax = Math.max(value_filter_absolute_min, Math.min(nextMax, value_filter_absolute_max));

        value_filter_min = Math.min(clampedMin, clampedMax);
        value_filter_max = Math.max(clampedMin, clampedMax);

        const sliderStep = (value_filter_absolute_max - value_filter_absolute_min) / 1000;
        const tolerance = Math.max(1e-5, sliderStep * 1.5);
        const isSticky = (Math.abs(value_filter_min - value_filter_absolute_min) <= tolerance) &&
                         (Math.abs(value_filter_max - value_filter_absolute_max) <= tolerance);

        remembered_value_filter_ranges[filterGroup] = {
          min: value_filter_min,
          max: value_filter_max,
          isSticky
        };

        controlBand.updateValueRangeControls({
          min: value_filter_absolute_min,
          max: value_filter_absolute_max,
          selectedMin: value_filter_min,
          selectedMax: value_filter_max,
          unit: value_filter_unit,
          decimals: value_filter_decimals,
          disabled: false
        });

        // Re-apply using loadData so dependent overlays and URL state stay in sync.
        await loadData(show_selection, show_location);
      }
    }
  };
  // Function to append message in control band
  const addMessage = (text) => {
    // Update text to support line breaks
    text = text.replace(/\n/g, '<br>');
    messageText = messageText + "<br>" + text;
    controlBand.messageArea.innerHTML = messageText;
    controlBand.messageArea.scrollTop = controlBand.messageArea.scrollHeight;
  };

  const updateSupplementalSpectrumPositions = () => {
    // Keep supplemental spectra stacked in a stable order above the base spectrum.
    let nextBottom = 320;
    [vorticitySpectrumArea, chlSpectrumArea, bgaSpectrumArea, mblhSpectrumArea].forEach((area) => {
      if (!area) return;
      area.style.bottom = `${nextBottom}px`;
      nextBottom += 80;
    });
  };

  const ensureMainSpectrumMarker = () => {
    // Ensure the main spectrum has a hover marker host (white border + black center line).
    const spectrumBarHost = document.getElementById('main-spectrum-bar');
    if (!spectrumBarHost) return null;

    let markerOutline = document.getElementById('main-spectrum-marker-outline');
    if (markerOutline) return markerOutline;

    markerOutline = document.createElement('div');
    markerOutline.id = 'main-spectrum-marker-outline';
    markerOutline.style.position = 'absolute';
    markerOutline.style.left = '1px';
    markerOutline.style.top = '-3px';
    markerOutline.style.bottom = '0';
    markerOutline.style.width = '2px';
    markerOutline.style.height = '133%';
    markerOutline.style.bottom = '-3px';
    markerOutline.style.background = '#000';
    markerOutline.style.pointerEvents = 'none';
    markerOutline.style.borderRight = "2px solid #FFF";
    markerOutline.style.borderLeft = "2px solid #FFF";

    spectrumBarHost.appendChild(markerOutline);
    return markerOutline;
  };

  const clearMainSpectrumIndicator = () => {
    const markerOutline = document.getElementById('main-spectrum-marker-outline');
    if (markerOutline) {
      markerOutline.style.display = 'none';
    }
  };

  const getMainSpectrumPosition = (value, metricRange, isSplitSpectrum) => {
    // Convert a metric value to a normalized [0,1] spectrum position, matching the active color-scale logic.
    if (!metricRange || !Number.isFinite(value) || !Number.isFinite(metricRange.min) || !Number.isFinite(metricRange.max)) {
      return null;
    }

    const minValue = metricRange.min;
    const maxValue = metricRange.max;
    if (maxValue === minValue) {
      return 0.5;
    }

    const clamp01 = (v) => Math.max(0, Math.min(1, v));

    if (isSplitSpectrum && metricRange.scale === scale_type.co2_delta) {
      if (minValue < 0 && maxValue > 0) {
        if (value < 0) {
          return clamp01(0.5 * ((value - minValue) / (0 - minValue)));
        }
        return clamp01(0.5 + 0.5 * (value / maxValue));
      }
      return clamp01((value - minValue) / (maxValue - minValue));
    }

    return clamp01((value - minValue) / (maxValue - minValue));
  };

  const updateMainSpectrumIndicator = (value, metricRange, isSplitSpectrum) => {
    // Move the main-spectrum marker to the hovered point's value.
    const markerOutline = ensureMainSpectrumMarker();
    if (!markerOutline) return;

    const normalizedPosition = getMainSpectrumPosition(value, metricRange, isSplitSpectrum);
    if (normalizedPosition === null) {
      markerOutline.style.display = 'none';
      return;
    }

    markerOutline.style.left = `${normalizedPosition * 100}%`;
    markerOutline.style.display = 'block';
  };

  const clearMainSpectrumHoverValue = () => {
    const hoverValue = document.getElementById('main-spectrum-hover-value');
    if (hoverValue) {
      hoverValue.innerHTML = '&nbsp;';
    }
  };

  const updateMainSpectrumHoverValue = (value, metricRange, showValue) => {
    const hoverValue = document.getElementById('main-spectrum-hover-value');
    if (!hoverValue || !metricRange || !Number.isFinite(value)) return;

    let metricLabel = controlBand.showDropdown.options[controlBand.showDropdown.selectedIndex]?.textContent || showValue;
    if (metricLabel === 'Avg 25km') {
      metricLabel = 'CO₂ Avg 25km';
    } else if (metricLabel === 'Avg 12km') {
      metricLabel = 'CO₂ Avg 12km';
    } else if (metricLabel === 'Raw') {
      metricLabel = 'CO₂ Raw';
    } else if (metricLabel === 'CO2 Max/Min') {
      metricLabel = 'CO₂ Avg 25km';
    } else if (metricLabel === 'Gradient Avg 25km Max/Min') {
      metricLabel = 'CO₂ Gradient Avg 25km';
    } 
    const formattedValue = value.toFixed(metricRange.decimals);
    hoverValue.textContent = `${metricLabel}: ${formattedValue} ${metricRange.unit}`;
  };

  const ensureVorticitySpectrumMarker = () => {
    // Ensure the vorticity spectrum has a hover marker host (white border + black center line).
    const spectrumBar = document.getElementById('vorticity-spectrum-bar');
    if (!spectrumBar) return null;

    let markerOutline = document.getElementById('vorticity-spectrum-marker-outline');
    if (markerOutline) return markerOutline;

    markerOutline = document.createElement('div');
    markerOutline.id = 'vorticity-spectrum-marker-outline';
    markerOutline.style.position = 'absolute';
    markerOutline.style.left = '1px';
    markerOutline.style.top = '-3px';
    markerOutline.style.bottom = '0';
    markerOutline.style.width = '2px';
    markerOutline.style.height = '133%';
    markerOutline.style.bottom = '-3px';
    markerOutline.style.background = '#000';
    markerOutline.style.pointerEvents = 'none';
    markerOutline.style.borderRight = '2px solid #FFF';
    markerOutline.style.borderLeft = '2px solid #FFF';

    spectrumBar.appendChild(markerOutline);
    return markerOutline;
  };

  const clearVorticitySpectrumIndicator = () => {
    const markerOutline = document.getElementById('vorticity-spectrum-marker-outline');
    if (markerOutline) {
      markerOutline.style.display = 'none';
    }
  };

  const updateVorticitySpectrumIndicator = (value, range) => {
    // Move the vorticity-spectrum marker to the hovered point value, clamped to spectrum edges.
    const markerOutline = ensureVorticitySpectrumMarker();
    if (!markerOutline || !range) return;

    if (!Number.isFinite(value) || !Number.isFinite(range.min) || !Number.isFinite(range.max)) {
      markerOutline.style.display = 'none';
      return;
    }

    const span = range.max - range.min;
    const normalizedPosition = span === 0
      ? 0.5
      : Math.max(0, Math.min(1, (value - range.min) / span));

    markerOutline.style.left = `${normalizedPosition * 100}%`;
    markerOutline.style.display = 'block';
  };

  const clearVorticityHoverValue = () => {
    const hoverValue = document.getElementById('vorticity-spectrum-hover-value');
    if (hoverValue) {
      hoverValue.innerHTML = '&nbsp;';
    }
  };

  const updateVorticityHoverValue = (value) => {
    const hoverValue = document.getElementById('vorticity-spectrum-hover-value');
    if (!hoverValue) return;

    const displayValue = Number.isFinite(value) ? (value * 1e6).toFixed(2) : 'N/A';
    hoverValue.textContent = `Vorticity at cursor: ${displayValue} E-6`;
  };

  const normalizeVorticityForSpectrum = (value) => {
    if (!Number.isFinite(value)) return null;
    const span = VORTICITY_SPECTRUM_MAX - VORTICITY_SPECTRUM_MIN;
    if (span === 0) return 0.5;
    return Math.max(0, Math.min(1, (value - VORTICITY_SPECTRUM_MIN) / span));
  };

  const getVorticityPointColor = (value) => {
    const t = normalizeVorticityForSpectrum(value);
    if (t === null) {
      return 'rgba(180, 180, 180, 0.8)';
    }

    const gradientStops = [
      [255, 0, 255],
      [0, 0, 255],
      [0, 255, 255],
      [0, 255, 0],
      [255, 255, 0],
      [255, 0, 0]
    ];

    const scaledIndex = t * (gradientStops.length - 1);
    const lowerIndex = Math.floor(scaledIndex);
    const upperIndex = Math.min(gradientStops.length - 1, lowerIndex + 1);
    const blend = scaledIndex - lowerIndex;
    const lower = gradientStops[lowerIndex];
    const upper = gradientStops[upperIndex];

    const r = Math.round(lower[0] + (upper[0] - lower[0]) * blend);
    const g = Math.round(lower[1] + (upper[1] - lower[1]) * blend);
    const b = Math.round(lower[2] + (upper[2] - lower[2]) * blend);
    return `rgba(${r}, ${g}, ${b}, 0.8)`;
  };

  const applyVorticityStylesForCurrentZoom = () => {
    // Scale vorticity point symbols with zoom so their apparent map size stays consistent.
    if (!vorticityFeatureSource) return;

    const view = map.getView();
    const zoom = view?.getZoom();
    const safeZoom = Number.isFinite(zoom) ? zoom : 2;
    const zoomScale = Math.pow(2, safeZoom - 5.8);
    const pointRadius = Number((5 * zoomScale).toFixed(2));

    const styleCache = new globalThis.Map();
    vorticityFeatureSource.getFeatures().forEach((feature) => {
      const vorticityValue = toFiniteNumber(feature.get('vorticity'));
      const color = getVorticityPointColor(vorticityValue);
      const cacheKey = `${pointRadius}_${color}`;
      if (!styleCache.has(cacheKey)) {
        styleCache.set(cacheKey, new Style({
          image: new Circle({
            radius: pointRadius,
            fill: new Fill({color}),
            stroke: new Stroke({color: 'rgba(0,0,0,0.2)', width: 0.5})
          })
        }));
      }
      feature.setStyle(styleCache.get(cacheKey));
    });
  };

  function getChlSpectrumSegment(chlValue) {
    if (!Number.isFinite(chlValue)) return null;

    const minValue = CHL_SPECTRUM_BOUNDS[0];
    const maxValue = CHL_SPECTRUM_BOUNDS[CHL_SPECTRUM_BOUNDS.length - 1];
    if (chlValue <= minValue) {
      return {index: 0, local: 0};
    }
    if (chlValue >= maxValue) {
      return {index: CHL_SPECTRUM_COLORS.length - 1, local: 1};
    }

    for (let i = 0; i < CHL_SPECTRUM_COLORS.length; i += 1) {
      const lower = CHL_SPECTRUM_BOUNDS[i];
      const upper = CHL_SPECTRUM_BOUNDS[i + 1];
      if (chlValue <= upper) {
        const span = upper - lower;
        const local = span === 0 ? 0 : (chlValue - lower) / span;
        return {index: i, local: Math.max(0, Math.min(1, local))};
      }
    }

    return {index: CHL_SPECTRUM_COLORS.length - 1, local: 1};
  }

  function chl_to_color(chl_value) {
    const segment = getChlSpectrumSegment(chl_value);
    if (!segment) return '#ffffff';
    return CHL_SPECTRUM_COLORS[segment.index];
  }

  function chl_to_marker_position(chl_value) {
    // Map CHL value to a normalized [0,1] marker position with linear interpolation inside each segment.
    const segment = getChlSpectrumSegment(chl_value);
    if (!segment) return 0;

    const segmentCount = CHL_SPECTRUM_COLORS.length;
    return Math.max(0, Math.min(1, (segment.index + segment.local) / segmentCount));
  }

  const buildChlSpectrumGradient = () => {
    const segmentCount = CHL_SPECTRUM_COLORS.length;
    const stops = CHL_SPECTRUM_COLORS.map((color, idx) => {
      const start = ((idx / segmentCount) * 100).toFixed(4);
      const end = (((idx + 1) / segmentCount) * 100).toFixed(4);
      return `${color} ${start}% ${end}%`;
    });
    return `linear-gradient(to right, ${stops.join(', ')})`;
  };

  const renderChlSpectrumLabels = (labelContainer) => {
    if (!labelContainer) return;
    labelContainer.innerHTML = '';
    labelContainer.style.display = 'grid';
    labelContainer.style.gridTemplateColumns = `repeat(${CHL_SPECTRUM_LABELS.length}, minmax(0, 1fr))`;
    labelContainer.style.columnGap = '0';

    CHL_SPECTRUM_LABELS.forEach((labelText) => {
      const label = document.createElement('span');
      label.textContent = labelText;
      label.style.textAlign = 'center';
      labelContainer.appendChild(label);
    });
  };

  const ensureChlSpectrumArea = () => {
    // Create CHL spectrum UI once and reuse it while CHL display is active.
    if (chlSpectrumArea) {
      updateSupplementalSpectrumPositions();
      return chlSpectrumArea;
    }

    chlSpectrumArea = document.createElement('div');
    chlSpectrumArea.id = 'chl-spectrum';
    chlSpectrumArea.style.position = 'fixed';
    chlSpectrumArea.style.right = '100px';
    chlSpectrumArea.style.padding = '5px 10px';
    chlSpectrumArea.style.minWidth = '320px';
    chlSpectrumArea.style.backgroundColor = '#f0f0f0';
    chlSpectrumArea.style.borderRadius = '4px';
    chlSpectrumArea.style.border = '1px solid #ccc';
    chlSpectrumArea.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    chlSpectrumArea.style.zIndex = '97';
    chlSpectrumArea.style.fontFamily = 'Arial, sans-serif';
    chlSpectrumArea.style.fontSize = '11px';

    const spectrumBar = document.createElement('div');
    spectrumBar.id = 'chl-spectrum-bar';
    spectrumBar.style.position = 'relative';
    spectrumBar.style.width = '100%';
    spectrumBar.style.height = '20px';
    spectrumBar.style.background = buildChlSpectrumGradient();
    chlSpectrumArea.appendChild(spectrumBar);

    const marker = document.createElement('div');
    marker.id = 'chl-spectrum-marker';
    marker.style.position = 'absolute';
    marker.style.top = '0';
    marker.style.bottom = '0';
    marker.style.width = '2px';
    marker.style.background = '#111';
    marker.style.display = 'none';
    spectrumBar.appendChild(marker);

    const labels = document.createElement('div');
    labels.id = 'chl-spectrum-labels';
    labels.style.marginTop = '4px';
    labels.style.fontSize = '10px';
    renderChlSpectrumLabels(labels);
    chlSpectrumArea.appendChild(labels);

    const title = document.createElement('div');
    title.id = 'chl-spectrum-title';
    title.style.display = 'flex';
    title.style.justifyContent = 'center';
    title.style.marginTop = '2px';
    title.textContent = 'Chl as of unknown';
    chlSpectrumArea.appendChild(title);

    const hoverValue = document.createElement('div');
    hoverValue.id = 'chl-spectrum-hover-value';
    hoverValue.style.display = 'flex';
    hoverValue.style.justifyContent = 'center';
    hoverValue.style.marginTop = '2px';
    hoverValue.style.minHeight = '1.2em';
    hoverValue.innerHTML = '&nbsp;';
    chlSpectrumArea.appendChild(hoverValue);

    document.body.appendChild(chlSpectrumArea);
    updateSupplementalSpectrumPositions();
    return chlSpectrumArea;
  };

  const clearChlHoverIndicator = () => {
    const marker = document.getElementById('chl-spectrum-marker');
    const hoverValue = document.getElementById('chl-spectrum-hover-value');
    if (marker) marker.style.display = 'none';
    if (hoverValue) hoverValue.innerHTML = '&nbsp;';
  };

  const updateChlHoverIndicator = (chlValue, normalizedValue) => {
    // Move marker and text to match hovered CHL value.
    const marker = document.getElementById('chl-spectrum-marker');
    const hoverValue = document.getElementById('chl-spectrum-hover-value');
    if (!marker || !hoverValue) return;
    marker.style.display = 'block';
    const position = chl_to_marker_position(chlValue);
    marker.style.left = `${Math.max(0, Math.min(1, position)) * 100}%`;
    hoverValue.textContent = `Chl: ${chlValue.toFixed(2)} mg/m3`;
  };

  const removeChlOverlay = ({emitMessage = false} = {}) => {
    // Shared CHL teardown routine used by checkbox toggles and load synchronization.
    if (chlResolutionListenerKey) {
      unByKey(chlResolutionListenerKey);
      chlResolutionListenerKey = null;
    }
    chlSource.clear();
    chlLayer.setVisible(false);
    current_chl_label = null;
    last_chl_data_date = null;
    clearChlHoverIndicator();

    if (chlSpectrumArea) {
      chlSpectrumArea.remove();
      chlSpectrumArea = null;
    }

    updateSupplementalSpectrumPositions();
    if (emitMessage) {
      updateMessage('Chl display removed');
    }
    updateSpectrumSummary();
  };

  const getBgaColorForValue = (value) => {
    if (!Number.isFinite(value)) return '#ffffff';
    const config = BGA_SPECTRUM_CONFIG.find((entry) => value >= entry.min && value < entry.max);
    if (!config) {
      return BGA_SPECTRUM_CONFIG[BGA_SPECTRUM_CONFIG.length - 1].color;
    }
    return config.color;
  };

  const getBgaMarkerPosition = (value) => {
    if (!Number.isFinite(value)) return 0;
    const bandIndex = BGA_SPECTRUM_CONFIG.findIndex((entry) => value >= entry.min && value < entry.max);
    const safeBandIndex = bandIndex === -1 ? BGA_SPECTRUM_CONFIG.length - 1 : bandIndex;
    const band = BGA_SPECTRUM_CONFIG[safeBandIndex];
    const bandWidth = 1 / BGA_SPECTRUM_CONFIG.length;

    if (!Number.isFinite(band.min) || !Number.isFinite(band.max)) {
      return (safeBandIndex + 0.5) * bandWidth;
    }

    const valueWithinBand = (value - band.min) / (band.max - band.min);
    return (safeBandIndex + Math.max(0, Math.min(1, valueWithinBand))) * bandWidth;
  };

  const renderBgaSpectrumLabels = (labelContainer) => {
    if (!labelContainer) return;
    labelContainer.innerHTML = '';
    labelContainer.style.display = 'grid';
    labelContainer.style.gridTemplateColumns = `repeat(${BGA_SPECTRUM_CONFIG.length}, minmax(0, 1fr))`;
    labelContainer.style.columnGap = '0';
    BGA_SPECTRUM_CONFIG.forEach((entry) => {
      const label = document.createElement('span');
      label.textContent = entry.label;
      label.style.textAlign = 'center';
      label.style.fontSize = '9px';
      labelContainer.appendChild(label);
    });
  };

  const ensureBgaSpectrumArea = () => {
    if (bgaSpectrumArea) {
      updateSupplementalSpectrumPositions();
      return bgaSpectrumArea;
    }

    bgaSpectrumArea = document.createElement('div');
    bgaSpectrumArea.id = 'bga-spectrum';
    bgaSpectrumArea.style.position = 'fixed';
    bgaSpectrumArea.style.right = '100px';
    bgaSpectrumArea.style.padding = '5px 10px';
    bgaSpectrumArea.style.minWidth = '320px';
    bgaSpectrumArea.style.backgroundColor = '#f0f0f0';
    bgaSpectrumArea.style.borderRadius = '4px';
    bgaSpectrumArea.style.border = '1px solid #ccc';
    bgaSpectrumArea.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    bgaSpectrumArea.style.zIndex = '96';
    bgaSpectrumArea.style.fontFamily = 'Arial, sans-serif';
    bgaSpectrumArea.style.fontSize = '11px';

    const spectrumBar = document.createElement('div');
    spectrumBar.id = 'bga-spectrum-bar';
    spectrumBar.style.position = 'relative';
    spectrumBar.style.width = '100%';
    spectrumBar.style.height = '20px';
    const spectrumGradient = BGA_SPECTRUM_CONFIG.map((entry, index) => {
      const start = ((index / BGA_SPECTRUM_CONFIG.length) * 100).toFixed(4);
      const end = (((index + 1) / BGA_SPECTRUM_CONFIG.length) * 100).toFixed(4);
      return `${entry.color} ${start}% ${end}%`;
    }).join(', ');
    spectrumBar.style.background = `linear-gradient(to right, ${spectrumGradient})`;
    bgaSpectrumArea.appendChild(spectrumBar);

    const marker = document.createElement('div');
    marker.id = 'bga-spectrum-marker';
    marker.style.position = 'absolute';
    marker.style.top = '0';
    marker.style.bottom = '0';
    marker.style.width = '2px';
    marker.style.background = '#111';
    marker.style.display = 'none';
    spectrumBar.appendChild(marker);

    const labels = document.createElement('div');
    labels.id = 'bga-spectrum-labels';
    labels.style.marginTop = '4px';
    renderBgaSpectrumLabels(labels);
    bgaSpectrumArea.appendChild(labels);

    const title = document.createElement('div');
    title.id = 'bga-spectrum-title';
    title.style.display = 'flex';
    title.style.justifyContent = 'center';
    title.style.marginTop = '2px';
    title.textContent = 'Blue-Green Algae absorption (E-10 620nm)';
    bgaSpectrumArea.appendChild(title);

    const hoverValue = document.createElement('div');
    hoverValue.id = 'bga-spectrum-hover-value';
    hoverValue.style.display = 'flex';
    hoverValue.style.justifyContent = 'center';
    hoverValue.style.marginTop = '2px';
    hoverValue.style.minHeight = '1.2em';
    hoverValue.innerHTML = '&nbsp;';
    bgaSpectrumArea.appendChild(hoverValue);

    document.body.appendChild(bgaSpectrumArea);
    updateSupplementalSpectrumPositions();
    return bgaSpectrumArea;
  };

  const clearBgaHoverIndicator = () => {
    const marker = document.getElementById('bga-spectrum-marker');
    const hoverValue = document.getElementById('bga-spectrum-hover-value');
    if (marker) marker.style.display = 'none';
    if (hoverValue) hoverValue.innerHTML = '&nbsp;';
  };

  const updateBgaHoverIndicator = (bgaValue) => {
    const marker = document.getElementById('bga-spectrum-marker');
    const hoverValue = document.getElementById('bga-spectrum-hover-value');
    if (!marker || !hoverValue) return;
    marker.style.display = 'block';
    marker.style.left = `${Math.max(0, Math.min(1, getBgaMarkerPosition(bgaValue))) * 100}%`;
    hoverValue.textContent = `BGA: ${bgaValue} E-10`;
  };

  const removeBgaOverlay = ({emitMessage = false} = {}) => {
    if (bgaResolutionListenerKey) {
      unByKey(bgaResolutionListenerKey);
      bgaResolutionListenerKey = null;
    }
    if (bgaFeatureSource) {
      bgaFeatureSource.clear();
    }
    if (bgaLayer) {
      bgaLayer.setVisible(false);
    }
    bgaFeatureSource = null;
    last_bga_data_date = null;
    clearBgaHoverIndicator();

    if (bgaSpectrumArea) {
      bgaSpectrumArea.remove();
      bgaSpectrumArea = null;
    }

    updateSupplementalSpectrumPositions();
    if (emitMessage) {
      updateMessage('BGA display removed');
    }
    updateSpectrumSummary();
  };

  const ensureMblhSpectrumArea = () => {
    if (mblhSpectrumArea) {
      updateSupplementalSpectrumPositions();
      return mblhSpectrumArea;
    }

    mblhSpectrumArea = document.createElement('div');
    mblhSpectrumArea.id = 'mblh-spectrum';
    mblhSpectrumArea.style.position = 'fixed';
    mblhSpectrumArea.style.right = '100px';
    mblhSpectrumArea.style.padding = '5px 10px';
    mblhSpectrumArea.style.minWidth = '320px';
    mblhSpectrumArea.style.backgroundColor = '#f0f0f0';
    mblhSpectrumArea.style.borderRadius = '4px';
    mblhSpectrumArea.style.border = '1px solid #ccc';
    mblhSpectrumArea.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    mblhSpectrumArea.style.zIndex = '95';
    mblhSpectrumArea.style.fontFamily = 'Arial, sans-serif';
    mblhSpectrumArea.style.fontSize = '11px';

    const stepCount = MBLH_SPECTRUM_COLORS.length;
    const stops = MBLH_SPECTRUM_COLORS.map((color, index) =>
      `${color} ${((index / stepCount) * 100).toFixed(4)}% ${(((index + 1) / stepCount) * 100).toFixed(4)}%`);
    const spectrumBar = document.createElement('div');
    spectrumBar.id = 'mblh-spectrum-bar';
    spectrumBar.style.position = 'relative';
    spectrumBar.style.width = '100%';
    spectrumBar.style.height = '20px';
    spectrumBar.style.background = `linear-gradient(to right, ${stops.join(', ')})`;
    mblhSpectrumArea.appendChild(spectrumBar);

    const marker = document.createElement('div');
    marker.id = 'mblh-spectrum-marker';
    marker.style.position = 'absolute';
    marker.style.top = '0';
    marker.style.bottom = '0';
    marker.style.width = '2px';
    marker.style.background = '#111';
    marker.style.display = 'none';
    spectrumBar.appendChild(marker);

    const labels = document.createElement('div');
    labels.style.display = 'flex';
    labels.style.justifyContent = 'space-between';
    labels.style.marginTop = '4px';
    labels.style.fontSize = '10px';
    labels.innerHTML = `<span>${MBLH_MIN}m</span><span>${MBLH_MAX}m</span>`;
    mblhSpectrumArea.appendChild(labels);

    const title = document.createElement('div');
    title.style.display = 'flex';
    title.style.justifyContent = 'center';
    title.style.marginTop = '2px';
    title.textContent = 'Marine Boundary Layer Height (MBLH)';
    mblhSpectrumArea.appendChild(title);

    const hoverValue = document.createElement('div');
    hoverValue.id = 'mblh-spectrum-hover-value';
    hoverValue.style.display = 'flex';
    hoverValue.style.justifyContent = 'center';
    hoverValue.style.marginTop = '2px';
    hoverValue.style.minHeight = '1.2em';
    hoverValue.innerHTML = '&nbsp;';
    mblhSpectrumArea.appendChild(hoverValue);

    // Time-of-day selector: one stop per ERA5 time column.
    const timeBox = document.createElement('div');
    timeBox.style.marginTop = '6px';
    timeBox.style.borderTop = '1px solid #ccc';
    timeBox.style.paddingTop = '4px';

    const timeLabel = document.createElement('div');
    timeLabel.style.textAlign = 'center';
    timeLabel.textContent = `Time: ${formatMblhTime(mblh_time)}`;

    const timeSlider = document.createElement('input');
    timeSlider.type = 'range';
    timeSlider.min = '0';
    timeSlider.max = String(MBLH_TIMES.length - 1);
    timeSlider.step = '1';
    timeSlider.value = String(MBLH_TIMES.indexOf(mblh_time));
    timeSlider.style.width = '100%';
    timeSlider.style.margin = '2px 0';
    timeSlider.title = 'Select the time of day for MBLH';

    const timeTicks = document.createElement('div');
    timeTicks.style.display = 'flex';
    timeTicks.style.justifyContent = 'space-between';
    timeTicks.style.fontSize = '10px';
    timeTicks.innerHTML = MBLH_TIMES.map((t) => `<span>${formatMblhTime(t)}</span>`).join('');

    timeSlider.addEventListener('input', () => {
      timeLabel.textContent = `Time: ${formatMblhTime(MBLH_TIMES[Number(timeSlider.value)])}`;
    });
    timeSlider.addEventListener('change', async () => {
      mblh_time = MBLH_TIMES[Number(timeSlider.value)];
      syncUrlWithCurrentState();
      await refreshMblhOverlayForDate(feature_display_date || earliest_displayed_orbit_date);
    });

    timeBox.appendChild(timeLabel);
    timeBox.appendChild(timeSlider);
    timeBox.appendChild(timeTicks);
    mblhSpectrumArea.appendChild(timeBox);

    document.body.appendChild(mblhSpectrumArea);
    updateSupplementalSpectrumPositions();
    return mblhSpectrumArea;
  };

  const clearMblhHoverIndicator = () => {
    const marker = document.getElementById('mblh-spectrum-marker');
    const hoverValue = document.getElementById('mblh-spectrum-hover-value');
    if (marker) marker.style.display = 'none';
    if (hoverValue) hoverValue.innerHTML = '&nbsp;';
  };

  const updateMblhHoverIndicator = (mblhValue) => {
    const marker = document.getElementById('mblh-spectrum-marker');
    const hoverValue = document.getElementById('mblh-spectrum-hover-value');
    if (!marker || !hoverValue || !Number.isFinite(mblhValue)) return;
    const position = Math.max(0, Math.min(1, (mblhValue - MBLH_MIN) / (MBLH_MAX - MBLH_MIN)));
    marker.style.left = `${position * 100}%`;
    marker.style.display = 'block';
    hoverValue.textContent = `MBLH ${Math.round(mblhValue)}m`;
  };

  const removeMblhOverlay = () => {
    mblhSource.clear();
    mblhLayer.setVisible(false);
    if (mblhSpectrumArea) {
      mblhSpectrumArea.remove();
      mblhSpectrumArea = null;
    }
    updateSupplementalSpectrumPositions();
    updateSpectrumSummary();
  };

  const refreshMblhOverlayForDate = async (dateValue) => {
    if (!show_mblh) return;
    const dateText = formatDateAsYYYYMMDD(dateValue);
    if (!dateText) {
      removeMblhOverlay();
      return;
    }

    try {
      const csvText = await fetchCsvText(`./data/${show_location}/era5/era5_${dateText}.csv`);
      if (!show_mblh) return;
      if (csvText === null) {
        removeMblhOverlay();
        addMessage(`No MBLH (ERA5) data for ${show_location} on ${dateText}`);
        return;
      }

      const styleCache = new globalThis.Map();
      const styleForColor = (color) => {
        if (!styleCache.has(color)) styleCache.set(color, new Style({fill: new Fill({color})}));
        return styleCache.get(color);
      };
      const half = 0.125; // data resolution is 0.25 degrees
      const mblhColumn = `mblh${mblh_time}`;
      const features = [];
      parseCSV(csvText).forEach((row) => {
        const lat = toFiniteNumber(row.latitude);
        const lon = toFiniteNumber(row.longitude);
        const mblh = toFiniteNumber(row[mblhColumn]);
        if (lat === null || lon === null || mblh === null) return;
        const ring = [[lon - half, lat - half], [lon + half, lat - half], [lon + half, lat + half], [lon - half, lat + half], [lon - half, lat - half]]
          .map((corner) => fromLonLat(corner));
        const feature = new Feature({geometry: new Polygon([ring]), isMblhPoint: true, mblhValue: mblh});
        feature.setStyle(styleForColor(getMblhColor(mblh)));
        features.push(feature);
      });

      if (features.length === 0) {
        // Keep the spectrum (and its time slider) so another time can be chosen.
        mblhSource.clear();
        ensureMblhSpectrumArea();
        clearMblhHoverIndicator();
        updateSpectrumSummary();
        addMessage(`No MBLH (ERA5) data at ${formatMblhTime(mblh_time)} for ${show_location} on ${dateText}`);
        return;
      }

      mblhSource.clear();
      mblhSource.addFeatures(features);
      mblhLayer.setVisible(true);
      ensureMblhSpectrumArea();
      clearMblhHoverIndicator();
      updateSupplementalSpectrumPositions();
      updateSpectrumSummary();
    } catch (error) {
      console.error('Error loading MBLH overlay:', error);
      removeMblhOverlay();
      addMessage(`Error loading MBLH overlay: ${error.message}`);
    }
  };

  const refreshBgaOverlayForDate = async (dateValue) => {
    if (!show_bga) return;
    if (!dateValue) {
      removeBgaOverlay({emitMessage: false});
      return;
    }

    const dateText = formatDateAsYYYYMMDD(dateValue);
    if (!dateText) {
      removeBgaOverlay({emitMessage: false});
      return;
    }

    const filePath = `./data/${show_location}/bga/bga_${dateText}.csv`;
    try {
      const csvText = await fetchCsvText(filePath);
      if (csvText === null) {
        removeBgaOverlay({emitMessage: false});
        addMessage(`No BGA data for ${show_location} on ${dateText}`);
        return;
      }

      const rows = parseCSV(csvText)
        .map((row) => ({
          date: row.date,
          latitude: parseFloat(row.latitude),
          longitude: parseFloat(row.longitude),
          Oa07_reflectance: toFiniteNumber(row.Oa07_reflectance)
        }))
        .filter((row) => Number.isFinite(row.latitude) && Number.isFinite(row.longitude) && Number.isFinite(row.Oa07_reflectance));

      if (rows.length === 0) {
        removeBgaOverlay({emitMessage: false});
        addMessage(`No BGA data for ${show_location} on ${dateText}`);
        return;
      }

      bgaSource.clear();
      rows.forEach((row) => {
        if (row.Oa07_reflectance != 0) {
          const feature = new Feature({
            geometry: new Point(fromLonLat([row.longitude, row.latitude])),
            isBgaPoint: true,
            bgaValue: row.Oa07_reflectance,
            bgaColor: getBgaColorForValue(row.Oa07_reflectance)
          });
          bgaSource.addFeature(feature);
        }
      });
      bgaFeatureSource = bgaSource;

      const styleCache = new globalThis.Map();
      const view = map.getView();
      const zoomLevel = view?.getZoom();
      const safeZoom = Number.isFinite(zoomLevel) ? zoomLevel : 2;
      const halfDiagonal = Number(5.3 * Math.pow(2, (safeZoom - 5.3))).toFixed(3);
      bgaSource.getFeatures().forEach((feature) => {
        const color = feature.get('bgaColor') || '#ffffff';
        const cacheKey = `${halfDiagonal}_${color}`;
        if (!styleCache.has(cacheKey)) {
          styleCache.set(cacheKey, new Style({
            image: new RegularShape({
              points: 4,
              radius: halfDiagonal,
              angle: Math.PI / 4,
              fill: new Fill({color}),
              // stroke: new Stroke({color: 'rgba(0,0,0,0.25)', width: 0.4})
            })
          }));
        }
        feature.setStyle(styleCache.get(cacheKey));
      });
      if (!bgaResolutionListenerKey) {
        bgaResolutionListenerKey = map.getView().on('change:resolution', () => {
          if (show_bga && bgaLayer?.getVisible()) {
            const currentView = map.getView();
            const zoomLevel = currentView?.getZoom();
            const safeLevel = Number.isFinite(zoomLevel) ? zoomLevel : 2;
            //const halfSize = Number(Math.max(3.6, Math.min(10, 4.2 * Math.pow(2, safeLevel - 5.3))).toFixed(2));
            const halfSize = Number(5.3 * Math.pow(2, (safeLevel - 5.3))).toFixed(2);
            const styleCache2 = new globalThis.Map();
            bgaSource.getFeatures().forEach((feature) => {
              const color = feature.get('bgaColor') || '#ffffff';
              const key = `${halfSize}_${color}`;
              if (!styleCache2.has(key)) {
                styleCache2.set(key, new Style({
                  image: new RegularShape({
                    points: 4,
                    radius: halfSize,
                    angle: Math.PI / 4,
                    fill: new Fill({color}),
                    // stroke: new Stroke({color: 'rgba(0,0,0,0.25)', width: 0.4})
                  })
                }));
              }
              feature.setStyle(styleCache2.get(key));
            });
          }
        });
      }

      bgaLayer.setVisible(true);
      const spectrum = ensureBgaSpectrumArea();
      const title = spectrum.querySelector('#bga-spectrum-title');
      if (title) title.textContent = 'Blue-Green Algae absorption (E-10 620nm)';
      renderBgaSpectrumLabels(spectrum.querySelector('#bga-spectrum-labels'));
      clearBgaHoverIndicator();
      last_bga_data_date = formatYYYYMMDDTextWithHyphens(dateText);
      updateSupplementalSpectrumPositions();
      updateSpectrumSummary();
    } catch (error) {
      console.error('Error loading BGA overlay:', error);
      removeBgaOverlay({emitMessage: false});
      addMessage(`Error loading BGA overlay: ${error.message}`);
    }
  };

  
  
  const applyChlStylesForCurrentZoom = () => {
    // Apply zoom-scaled square styles with defensive defaults to avoid render-time failures.
    const view = map.getView();
    const zoom = view?.getZoom();
    const safeZoom = Number.isFinite(zoom) ? zoom : 2;
    // Keep CHL symbols large enough to be clearly visible over CO2 points, while still scaling with zoom.
    const zoomScale = Math.pow(2, (safeZoom - 5.3));
    // const halfDiagonal = Number(Math.max(3.2, Math.min(16, 5.5 * zoomScale)).toFixed(2));
    const halfDiagonal = Number(5.3 * zoomScale).toFixed(2);
    // console.log(`applyChlStylesForCurrentZoom: zoom=${safeZoom}, zoomscale = ${zoomScale}, halfDiagonal=${halfDiagonal}`);

    const styleCache = new globalThis.Map();
    chlSource.getFeatures().forEach((feature) => {
      const color = feature.get('chlColor') || '#e0ff4f';
      const cacheKey = `${halfDiagonal}_${color}`;
      if (!styleCache.has(cacheKey)) {
        styleCache.set(cacheKey, new Style({
          image: new RegularShape({
            points: 4,
            radius: halfDiagonal,
            // Rotate 45 deg because a 4-point regular shape defaults to a diamond shape.
            angle: Math.PI / 4,
            fill: new Fill({color}),
            // stroke: new Stroke({color: 'black', width: 0.5})
          })
        }));
      }
      feature.setStyle(styleCache.get(cacheKey));
    });
  };

  const refreshChlOverlayForDate = async (dateValue) => {
    // Load CHL data for an explicit target date.
    if (!show_chl) return;
    if (!dateValue) {
      removeChlOverlay({emitMessage: false});
      return;
    }

    try {
      const manifestText = await ensureChlManifestText(show_location);
      if (!manifestText) {
        removeChlOverlay({emitMessage: false});
        addMessage(`Error: Could not load Chl manifest for ${show_location}`);
        return;
      }

      // Step 1: choose nearest 8d file first, then nearest 1d if no 8d exists.
      const selectedFile = selectNearestChlFile(manifestText, dateValue);
      if (!selectedFile) {
        removeChlOverlay({emitMessage: false});
        addMessage('No Chl file could be selected from chl_days.txt');
        return;
      }

      // Step 2: load and parse selected CHL file.
      const csvPath = `./data/${show_location}/${chl_folder}/${selectedFile.filename}`;
      const csvText = await fetchCsvText(csvPath);
      if (csvText === null) {
        removeChlOverlay({emitMessage: false});
        addMessage(`Error: Could not load Chl file ${csvPath}`);
        return;
      }

      const rows = parseChlCsvRows(csvText);
      const chlRange = computeChlRange(rows, 0.01);
      if (!chlRange) {
        removeChlOverlay({emitMessage: false});
        addMessage('No Chl values above 0.01 were found in selected file');
        return;
      }

      // Step 3: render semi-transparent CHL layer, with intensity scaled by CHL range.
      chlSource.clear();
      rows.forEach((row) => {
        const normalized = normalizeChl(row.chl, chlRange);
        if (normalized === null) return;

        const feature = new Feature({
          geometry: new Point(fromLonLat([row.longitude, row.latitude])),
          isChlPoint: true,
          chlValue: row.chl,
          chlNorm: normalized,
          chlColor: chl_to_color(row.chl)
        });
        chlSource.addFeature(feature);
      });

      if (chlSource.getFeatures().length === 0) {
        removeChlOverlay({emitMessage: false});
        addMessage('No Chl points survived threshold > 0.01');
        return;
      }

      // Step 3b: apply zoom-aware square symbols and keep them synchronized with map zoom.
      applyChlStylesForCurrentZoom();
      if (!chlResolutionListenerKey) {
        chlResolutionListenerKey = map.getView().on('change:resolution', () => {
          if (show_chl && chlLayer.getVisible()) {
            applyChlStylesForCurrentZoom();
          }
        });
      }

      chlLayer.setVisible(true);

      // Step 4: update CHL spectrum labels/title. The Chl date/label is shown in the Chl spectrum, not the main message area.
      const spectrum = ensureChlSpectrumArea();
      const labels = spectrum.querySelector('#chl-spectrum-labels');
      const title = spectrum.querySelector('#chl-spectrum-title');
      renderChlSpectrumLabels(labels);

      current_chl_label = formatChlLabel(selectedFile);
      last_chl_data_date = formatYYYYMMDDTextWithHyphens(selectedFile.dateText);
      if (title) {
        title.textContent = current_chl_label;
      }

      clearChlHoverIndicator();
      updateSupplementalSpectrumPositions();
      updateSpectrumSummary();
    } catch (error) {
      console.error('Error loading Chl overlay:', error);
      removeChlOverlay({emitMessage: false});
      addMessage(`Error loading Chl overlay: ${error.message}`);
    }
  };

  const vorticityCheckboxControl = controlBand.checkboxes.find(cb => cb.variable === 'show_vorticity');

  const setVorticityCheckboxState = (checked) => {
    // Keep UI checkbox and state variable synchronized for programmatic vorticity actions.
    show_vorticity = checked;
    if (vorticityCheckboxControl?.element) {
      vorticityCheckboxControl.element.checked = checked;
    }
  };

  const removeVorticityDisplay = ({emitMessage = false} = {}) => {
    // Shared teardown routine used by checkbox toggles and refresh logic.
    if (vorticityHeatmapLayer) {
      if (vorticityHeatmapLayer.resolutionListenerKey) {
        unByKey(vorticityHeatmapLayer.resolutionListenerKey);
      }
      map.removeLayer(vorticityHeatmapLayer);
      vorticityHeatmapLayer = null;
    }
    vorticityFeatureSource = null;

    if (vorticitySpectrumArea) {
      vorticitySpectrumArea.remove();
      vorticitySpectrumArea = null;
    }
    current_vorticity_range = null;
    clearVorticityHoverValue();

    // Keep vorticity current arrows available for independent Current Arrows display.
    updateCurrentArrowLayerVisibility();

    if (emitMessage) {
      updateMessage('Vorticity display removed');
    }
    updateSupplementalSpectrumPositions();
    updateSpectrumSummary();
  };

  const renderVorticityDisplay = (filteredData, dateNoHyphens) => {
    // Step 1: clear old vorticity display before rendering the new date/region.
    removeVorticityDisplay({emitMessage: false});
    currentSource.clear();
    updateCurrentArrowLayerVisibility();

    // Step 2: build vector sources for vorticity points and current arrows.
    const vorticityPointSource = new VectorSource();

    filteredData.forEach(row => {
      const lat = parseFloat(row.lat);
      const lon = parseFloat(row.lon);
      const vorticity = parseFloat(row.vorticity);
      const u_current = parseFloat(row.u_current);
      const v_current = parseFloat(row.v_current);

      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        vorticity,
        isVorticityPoint: true
      });

      vorticityPointSource.addFeature(feature);

      if (!isNaN(u_current) && !isNaN(v_current)) {
        const current_direction = getWindDirection(u_current, v_current);
        const current_magnitude = Math.sqrt(Math.pow(parseFloat(u_current) || 0, 2) + Math.pow(parseFloat(v_current) || 0, 2));

        const arrowFeatures = createArrowFeature([lon, lat], current_direction, current_magnitude, 1.0, 2, 0.0, 1.0, '#1E90FF');
        if (arrowFeatures) {
          arrowFeatures.forEach(feature => {
            feature.setProperties({
              arrowType: 'current',
              u_current,
              v_current,
              current_speed_kmh: current_magnitude * 3.6
            });
            currentSource.addFeature(feature);
          });
        }
      }
    });

    // Step 3: create and add the vorticity point layer.
    vorticityHeatmapLayer = new VectorLayer({
      source: vorticityPointSource,
      declutter: false,
      zIndex: 119,
      opacity: 0.5,
    });
    map.addLayer(vorticityHeatmapLayer);
    vorticityFeatureSource = vorticityPointSource;
    applyVorticityStylesForCurrentZoom();

    const resolutionListenerKey = map.getView().on('change:resolution', () => {
      if (show_vorticity && vorticityHeatmapLayer?.getVisible()) {
        applyVorticityStylesForCurrentZoom();
      }
    });
    vorticityHeatmapLayer.resolutionListenerKey = resolutionListenerKey;
    updateCurrentArrowLayerVisibility();

    // Step 4: refresh vorticity spectrum UI for the new dataset.
    vorticitySpectrumArea = document.createElement('div');
    vorticitySpectrumArea.id = 'vorticity-spectrum';
    vorticitySpectrumArea.style.position = 'fixed';
    vorticitySpectrumArea.style.right = '100px';
    vorticitySpectrumArea.style.bottom = '300px';
    vorticitySpectrumArea.style.padding = '5px 10px';
    vorticitySpectrumArea.style.minWidth = '320px';
    vorticitySpectrumArea.style.backgroundColor = '#f0f0f0';
    vorticitySpectrumArea.style.borderRadius = '4px';
    vorticitySpectrumArea.style.border = '1px solid #ccc';
    vorticitySpectrumArea.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    vorticitySpectrumArea.style.zIndex = '98';
    vorticitySpectrumArea.style.fontFamily = 'Arial, sans-serif';
    vorticitySpectrumArea.style.fontSize = '11px';

    const spectrumBar = document.createElement('div');
    spectrumBar.id = 'vorticity-spectrum-bar';
    spectrumBar.style.position = 'relative';
    spectrumBar.style.width = '100%';
    spectrumBar.style.height = '20px';
    spectrumBar.style.background = 'linear-gradient(to right, rgb(255,0,255), rgb(0,0,255), rgb(0,255,255), rgb(0,255,0), rgb(255,255,0), rgb(255,0,0))';
 
    
    vorticitySpectrumArea.appendChild(spectrumBar);

    ensureVorticitySpectrumMarker();
    clearVorticitySpectrumIndicator();
    current_vorticity_range = { min: VORTICITY_SPECTRUM_MIN, max: VORTICITY_SPECTRUM_MAX };

    const spectrumLabels = document.createElement('div');
    spectrumLabels.style.display = 'flex';
    spectrumLabels.style.justifyContent = 'space-between';
    spectrumLabels.style.marginTop = '4px';
    spectrumLabels.innerHTML = `<span>${(VORTICITY_SPECTRUM_MIN * 1e6).toFixed(3)}</span><span>${(VORTICITY_SPECTRUM_MAX * 1e6).toFixed(3)}</span>`;
    vorticitySpectrumArea.appendChild(spectrumLabels);

    const spectrumLabels2 = document.createElement('div');
    spectrumLabels2.style.display = 'flex';
    spectrumLabels2.style.justifyContent = 'center';
    spectrumLabels2.style.marginTop = '2px';
    spectrumLabels2.innerHTML = '<span style="font-size:10px;">Vorticity Spectrum (x E-6)</span>';
    vorticitySpectrumArea.appendChild(spectrumLabels2);

    const hoverValue = document.createElement('div');
    hoverValue.id = 'vorticity-spectrum-hover-value';
    hoverValue.style.display = 'flex';
    hoverValue.style.justifyContent = 'center';
    hoverValue.style.marginTop = '2px';
    hoverValue.style.minHeight = '1.2em';
    hoverValue.innerHTML = '&nbsp;';
    vorticitySpectrumArea.appendChild(hoverValue);

    document.body.appendChild(vorticitySpectrumArea);
    updateSupplementalSpectrumPositions();

    last_vorticity_date = `${dateNoHyphens.substring(0, 4)}-${dateNoHyphens.substring(4, 6)}-${dateNoHyphens.substring(6, 8)}`;
    updateSpectrumSummary();
  };

  const loadVorticityDataFile = async ({dateNoHyphens, location}) => {
    // Load one exact vorticity date file and parse CSV rows.
    const filePath = `./data/${location}/vorticity/${dateNoHyphens}_vorticity.csv`;
    const csvText = await fetchCsvText(filePath);
    if (csvText === null) {
      return {
        ok: false,
        filePath,
        data: null
      };
    }

    return {
      ok: true,
      filePath,
      data: parseCSV(csvText)
    };
  };

  const loadVorticityDataWithFallback = async ({dateValue, location}, fallback_date = null) => {
    // Try the requested date first, then the next day.
    let candidateDates = [new Date(dateValue),  addDays(dateValue, 1)];
    if (fallback_date) {
      candidateDates = candidateDates.concat([new Date(fallback_date), addDays(new Date(fallback_date), 1)]);
    }
    let lastLoadError = null;

    for (let index = 0; index < candidateDates.length; index += 1) {
      const candidateDateNoHyphens = formatDateAsYYYYMMDD(candidateDates[index]);
      if (!candidateDateNoHyphens) {
        continue;
      }

      try {
        const result = await loadVorticityDataFile({
          dateNoHyphens: candidateDateNoHyphens,
          location
        });

        if (result.ok) {
          return {
            data: result.data,
            dateNoHyphens: candidateDateNoHyphens,
            usedFallback: index > 0,
            filePath: result.filePath
          };
        }

        lastLoadError = `Error: Could not load vorticity file ${result.filePath}`;
      } catch (error) {
        console.error('Error loading vorticity data:', error);
        lastLoadError = `Error loading vorticity: ${error.message}`;
      }
    }

    return {
      data: null,
      dateNoHyphens: null,
      usedFallback: false,
      error: lastLoadError || 'Error loading vorticity data'
    };
  };

  /**
   * Ensure popup vorticity cache is loaded for a target date and location.
   * @param {Object} params Loader parameters.
   * @param {Date} params.dateValue Target date associated with the CO2 point.
   * @param {string} params.location Active location name.
   * @param {Date|null} [params.fallbackDate=null] Optional fallback date anchor.
   * @returns {Promise<void>} Resolves when cache has been updated/cleared.
   */
  const ensurePopupVorticityDataForDate = async ({dateValue, location, fallbackDate = null} = {}) => {
    // Step 1: validate date and skip reload when the needed cache is already available.
    const targetDateText = formatDateAsYYYYMMDDWithHyphens(dateValue);
    if (!targetDateText) {
      popupVorticityPointSource = null;
      popupVorticityDate = null;
      return;
    }

    if (popupVorticityPointSource && popupVorticityDate === targetDateText) {
      return;
    }

    // Step 2: load vorticity rows using the same fallback behavior as the overlay.
    const loadResult = await loadVorticityDataWithFallback({dateValue, location}, fallbackDate);
    if (!loadResult.data) {
      popupVorticityPointSource = null;
      popupVorticityDate = null;
      return;
    }

    // Step 3: convert valid rows into a vector source for nearest-point lookup.
    const source = new VectorSource();
    loadResult.data.forEach((row) => {
      const lat = parseFloat(row.lat);
      const lon = parseFloat(row.lon);
      const vorticity = toFiniteNumber(row.vorticity);
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || vorticity === null) {
        return;
      }

      source.addFeature(new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        vorticity
      }));
    });

    // Step 4: store cache source and the effective loaded date for date gating.
    popupVorticityPointSource = source;
    popupVorticityDate = loadResult.dateNoHyphens
      ? `${loadResult.dateNoHyphens.substring(0, 4)}-${loadResult.dateNoHyphens.substring(4, 6)}-${loadResult.dateNoHyphens.substring(6, 8)}`
      : null;
  };

  /**
   * Build gradient-vs-Vorticity scatter points for the graph tool. Loads vorticity from the date-based files for
   * each selected point's own date (reusing loadVorticityDataWithFallback), skipping points with no vorticity
   * value within 50km. This never touches popupVorticityPointSource/popupVorticityDate or the as-of overlay state,
   * so other vorticity-driven displays are unaffected.
   * @param {Array<Object>} rows Selected CO2 rows with latitude/longitude/_date and the gradient field populated.
   * @param {string} gradientField Row property name holding the gradient value to plot on the x axis.
   * @returns {Promise<{points: Array<{x:number,y:number}>, skippedCount: number}>} Plotted points and skip count.
   */
  const computeGradientVorticityPoints = async (rows, gradientField) => {
    const points = [];
    let skippedCount = 0;

    for (const row of rows) {
      const gradientValue = toFiniteNumber(row[gradientField]);
      const latitude = toFiniteNumber(row.latitude);
      const longitude = toFiniteNumber(row.longitude);
      const dateValue = row._date instanceof Date ? row._date : new Date(row._date);
      if (gradientValue === null || latitude === null || longitude === null || Number.isNaN(dateValue?.getTime?.())) {
        skippedCount += 1;
        continue;
      }

      // Reuse the same per-file cache across points that fall on the same date to avoid re-fetching.
      const dateNoHyphens = formatDateAsYYYYMMDD(dateValue);
      const cacheKey = `${show_location}|${dateNoHyphens}`;
      let vorticityRows = vorticityFileRowsCache.get(cacheKey);
      if (vorticityRows === undefined) {
        const loadResult = await loadVorticityDataWithFallback({dateValue, location: show_location});
        vorticityRows = (loadResult.data || [])
          .map((rawRow) => ({
            latitude: parseFloat(rawRow.lat),
            longitude: parseFloat(rawRow.lon),
            vorticity: toFiniteNumber(rawRow.vorticity)
          }))
          .filter((parsedRow) => Number.isFinite(parsedRow.latitude) && Number.isFinite(parsedRow.longitude) && parsedRow.vorticity !== null);
        vorticityFileRowsCache.set(cacheKey, vorticityRows);
      }

      let nearestVorticity = null;
      let nearestDistanceKm = Infinity;
      vorticityRows.forEach((vRow) => {
        const distanceKm = haversineDistanceKm(latitude, longitude, vRow.latitude, vRow.longitude);
        if (distanceKm < nearestDistanceKm) {
          nearestDistanceKm = distanceKm;
          nearestVorticity = vRow.vorticity;
        }
      });

      if (nearestVorticity === null || nearestDistanceKm > 50) {
        skippedCount += 1;
        continue;
      }
      points.push({x: gradientValue, y: nearestVorticity});
    }

    return {points, skippedCount};
  };

  // Look up vorticity rows for a point's date, reusing the same date-file cache as computeGradientVorticityPoints.
  const getVorticityRowsForDate = async (dateValue) => {
    const dateNoHyphens = formatDateAsYYYYMMDD(dateValue);
    const cacheKey = `${show_location}|${dateNoHyphens}`;
    let vorticityRows = vorticityFileRowsCache.get(cacheKey);
    if (vorticityRows === undefined) {
      const loadResult = await loadVorticityDataWithFallback({dateValue, location: show_location});
      vorticityRows = (loadResult.data || [])
        .map((rawRow) => ({
          latitude: parseFloat(rawRow.lat),
          longitude: parseFloat(rawRow.lon),
          vorticity: toFiniteNumber(rawRow.vorticity)
        }))
        .filter((parsedRow) => Number.isFinite(parsedRow.latitude) && Number.isFinite(parsedRow.longitude) && parsedRow.vorticity !== null);
      vorticityFileRowsCache.set(cacheKey, vorticityRows);
    }
    return vorticityRows;
  };

  /**
   * Build gradient-vs-(Chl x Vorticity) scatter points for the graph tool. Loads Chl (nearest 8d/1d file) and
   * Vorticity (date-based file with fallback) independently for each point's own date - the same lookups used by
   * the Gradient vs Chl and Gradient vs Vorticity graphs - then multiplies the two nearest values together.
   * Points are skipped if either value has no data within 50km. Neither lookup touches the single-slot popup/as-of
   * caches, so other Chl/Vorticity-driven displays are unaffected.
   * @param {Array<Object>} rows Selected CO2 rows with latitude/longitude/_date and the gradient field populated.
   * @param {string} gradientField Row property name holding the gradient value to plot on the x axis.
   * @returns {Promise<{points: Array<{x:number,y:number}>, skippedCount: number}>} Plotted points and skip count.
   */
  const computeGradientChlVorticityPoints = async (rows, gradientField) => {
    const points = [];
    let skippedCount = 0;

    // Chl needs the location's manifest to resolve the nearest 8d/1d file per point date.
    const manifestText = await ensureChlManifestText(show_location);
    if (!manifestText) {
      return {points, skippedCount: rows.length};
    }

    for (const row of rows) {
      const gradientValue = toFiniteNumber(row[gradientField]);
      const latitude = toFiniteNumber(row.latitude);
      const longitude = toFiniteNumber(row.longitude);
      const dateValue = row._date instanceof Date ? row._date : new Date(row._date);
      if (gradientValue === null || latitude === null || longitude === null || Number.isNaN(dateValue?.getTime?.())) {
        skippedCount += 1;
        continue;
      }

      // Resolve the nearest Chl value for this point's date.
      const selectedChlFile = selectNearestChlFile(manifestText, dateValue);
      let nearestChl = null;
      let nearestChlDistanceKm = Infinity;
      if (selectedChlFile) {
        const chlRows = await loadChlFileRows(show_location, selectedChlFile);
        chlRows.forEach((chlRow) => {
          const distanceKm = haversineDistanceKm(latitude, longitude, chlRow.latitude, chlRow.longitude);
          if (distanceKm < nearestChlDistanceKm) {
            nearestChlDistanceKm = distanceKm;
            nearestChl = chlRow.chl;
          }
        });
      }

      // Resolve the nearest Vorticity value for this point's date.
      const vorticityRows = await getVorticityRowsForDate(dateValue);
      let nearestVorticity = null;
      let nearestVorticityDistanceKm = Infinity;
      vorticityRows.forEach((vRow) => {
        const distanceKm = haversineDistanceKm(latitude, longitude, vRow.latitude, vRow.longitude);
        if (distanceKm < nearestVorticityDistanceKm) {
          nearestVorticityDistanceKm = distanceKm;
          nearestVorticity = vRow.vorticity;
        }
      });

      // Skip the point unless both Chl and Vorticity have data within 50km.
      if (nearestChl === null || nearestChlDistanceKm > 50 || nearestVorticity === null || nearestVorticityDistanceKm > 50) {
        skippedCount += 1;
        continue;
      }
      points.push({x: gradientValue, y: nearestChl * nearestVorticity});
    }

    return {points, skippedCount};
  };

  /**
   * Ensure popup CHL cache is loaded for a target date and location.
   * @param {Object} params Loader parameters.
   * @param {Date} params.dateValue Target date associated with the CO2 point.
   * @param {string} params.location Active location name.
   * @returns {Promise<void>} Resolves when cache has been updated/cleared.
   */
  const ensurePopupChlDataForDate = async ({dateValue, location} = {}) => {
    // Step 1: validate date and skip reload when the needed cache is already available.
    const targetDateText = formatDateAsYYYYMMDDWithHyphens(dateValue);
    if (!targetDateText) {
      popupChlPointSource = null;
      popupChlDate = null;
      return;
    }

    if (popupChlPointSource && popupChlDate === targetDateText) {
      return;
    }

    try {
      // Step 2: resolve nearest CHL file for this date using the manifest.
      const manifestText = await ensureChlManifestText(location);
      if (!manifestText) {
        popupChlPointSource = null;
        popupChlDate = null;
        return;
      }

      const selectedFile = selectNearestChlFile(manifestText, dateValue);
      if (!selectedFile) {
        popupChlPointSource = null;
        popupChlDate = null;
        return;
      }

      const csvPath = `./data/${location}/${chl_folder}/${selectedFile.filename}`;
      const chlCsvText = await fetchCsvText(csvPath);
      if (chlCsvText === null) {
        popupChlPointSource = null;
        popupChlDate = null;
        return;
      }

      // Step 3: convert valid CHL rows into a vector source for nearest-point lookup.
      const rows = parseChlCsvRows(chlCsvText);
      const source = new VectorSource();
      rows.forEach((row) => {
        if (!Number.isFinite(row.latitude) || !Number.isFinite(row.longitude) || !Number.isFinite(row.chl)) {
          return;
        }
        source.addFeature(new Feature({
          geometry: new Point(fromLonLat([row.longitude, row.latitude])),
          chlValue: row.chl
        }));
      });

      // Step 4: store cache source and the effective CHL file date for hover gating.
      popupChlPointSource = source;
      popupChlDate = formatYYYYMMDDTextWithHyphens(selectedFile.dateText);
    } catch (error) {
      console.warn('CHL popup cache load failed:', error);
      popupChlPointSource = null;
      popupChlDate = null;
    }
  };

  /**
   * Read nearest supplemental vorticity/CHL values for a CO2 feature.
   * @param {Feature} feature CO2 feature used for coordinate/date lookup.
   * @param {Object} options Behavior options.
   * @param {boolean} [options.requireDateMatch=true] If true, only use sources whose loaded date matches the point's effective date.
   * @returns {{vorticityValue: number|null, chlValue: number|null}} Nearest supplemental values for popup display.
   */
  const getPopupSupplementalValuesForFeature = (feature, {requireDateMatch = true} = {}) => {
    // Step 1: build lookup coordinate from feature lon/lat to avoid geometry-side projection/wrap drift.
    const featureLon = toFiniteNumber(feature?.get('longitude'));
    const featureLat = toFiniteNumber(feature?.get('latitude'));
    let coords = null;
    if (featureLon !== null && featureLat !== null) {
      coords = fromLonLat([featureLon, featureLat]);
    } else {
      // Fallback for any non-standard feature that does not carry lon/lat properties.
      const geometry = feature?.getGeometry?.();
      coords = geometry?.getCoordinates?.() || null;
    }

    const soundingId = feature?.get('sounding_id');
    const pointDateText = formatDateAsYYYYMMDDWithHyphens(parseDateFromSoundingId(soundingId));
    if (!coords || !pointDateText) {
      return {vorticityValue: null, chlValue: null};
    }

    // Step 2: select the best vorticity source for this point and mode.
    let vorticitySource = null;
    if (popupVorticityPointSource && popupVorticityDate === pointDateText) {
      vorticitySource = popupVorticityPointSource;
    } else if (vorticityFeatureSource && last_vorticity_date === pointDateText) {
      vorticitySource = vorticityFeatureSource;
    } else if (!requireDateMatch) {
      vorticitySource = popupVorticityPointSource || vorticityFeatureSource;
    }
    let vorticityValue = null;
    if (vorticitySource) {
      const nearest = vorticitySource.getClosestFeatureToCoordinate(coords);
      vorticityValue = toFiniteNumber(nearest?.get('vorticity'));
    }

    // Step 3: map point date to the nearest CHL file date (8-day/1-day as available).
    const nearestChlDateText = getNearestChlDateForPointDate(pointDateText, show_location) || pointDateText;

    // Step 4: select CHL source whose effective date matches this point's nearest CHL date.
    let chlSourceForPopup = null;
    if (popupChlPointSource && popupChlDate === nearestChlDateText) {
      chlSourceForPopup = popupChlPointSource;
    } else if (chlSource && last_chl_data_date === nearestChlDateText) {
      chlSourceForPopup = chlSource;
    } else if (!requireDateMatch) {
      chlSourceForPopup = popupChlPointSource || chlSource;
    }
    let chlValue = null;
    if (chlSourceForPopup) {
      const nearest = chlSourceForPopup.getClosestFeatureToCoordinate(coords);
      chlValue = toFiniteNumber(nearest?.get('chlValue'));
    }

    // Step 5: return both optional values for popup rendering.
    return {vorticityValue, chlValue};
  };

  const showVorticityForDate = async ({dateValue, location, areaFilter}) => {
    // Shared loader used by checkbox toggles, point clicks, and popup vorticity actions.
    const baseDateNoHyphens = formatDateAsYYYYMMDD(dateValue);
    if (!baseDateNoHyphens) {
      updateMessage('No valid date is available for vorticity display');
      return;
    }

    // Step 1: load vorticity data with one-day fallback.
    const loadResult = await loadVorticityDataWithFallback({dateValue, location});
    if (!loadResult.data) {
      addMessage(loadResult.error || 'Error loading vorticity data');
      return;
    }

    // Step 2: apply optional area filter.
    const filteredData = loadResult.data.filter(row => {
      const rowLat = parseFloat(row.lat);
      const rowLon = parseFloat(row.lon);
      if (!Number.isFinite(rowLat) || !Number.isFinite(rowLon) || row.vorticity === '') {
        return false;
      }
      if (typeof areaFilter === 'function') {
        return areaFilter(rowLat, rowLon);
      }
      return true;
    });

    if (filteredData.length === 0) {
      addMessage('No vorticity data found for the selected date/area');
      return;
    }

    // Step 3: render loaded dataset.
    renderVorticityDisplay(filteredData, loadResult.dateNoHyphens);
    if (loadResult.usedFallback) {
      addMessage(`Primary vorticity date unavailable, loaded next day ${loadResult.dateNoHyphens}`);
    }
  };

  const loadCurrentArrowsForDate = async ({dateValue, location, fallbackDate = null, emitNoDateMessage = true} = {}) => {
    // Load current arrows from vorticity files for one target date.
    if (!show_current_arrows) {
      updateCurrentArrowLayerVisibility();
      updateSpectrumSummary();
      return null;
    }

    if (!dateValue) {
      currentSource.clear();
      updateCurrentArrowLayerVisibility();
      updateSpectrumSummary();
      if (emitNoDateMessage) {
        addMessage('No displayed orbit is available for current arrows');
      }
      return null;
    }

    const loadResult = await loadVorticityDataWithFallback({
      dateValue,
      location
    }, fallbackDate);

    if (!loadResult.data) {
      currentSource.clear();
      updateCurrentArrowLayerVisibility();
      updateSpectrumSummary();
      addMessage(loadResult.error || 'Error loading current arrows');
      return null;
    }

    currentSource.clear();

    loadResult.data.forEach(row => {
      const lat = parseFloat(row.lat);
      const lon = parseFloat(row.lon);
      const u_current = parseFloat(row.u_current);
      const v_current = parseFloat(row.v_current);

      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        return;
      }

      if (!Number.isNaN(u_current) && !Number.isNaN(v_current)) {
        const current_direction = getWindDirection(u_current, v_current);
        const current_magnitude = Math.sqrt(Math.pow(parseFloat(u_current) || 0, 2) + Math.pow(parseFloat(v_current) || 0, 2));

        const arrowFeatures = createArrowFeature([lon, lat], current_direction, current_magnitude, 1.0, 2, 0.0, 1.0, '#1E90FF');
        if (arrowFeatures) {
          arrowFeatures.forEach(feature => {
            feature.setProperties({
              arrowType: 'current',
              u_current,
              v_current,
              current_speed_kmh: current_magnitude * 3.6
            });
            currentSource.addFeature(feature);
          });
        }
      }
    });

    updateCurrentArrowLayerVisibility();
    updateSpectrumSummary();
    return loadResult.dateNoHyphens;
  };

  const ensureWindAreaDataForLocation = async (location, dateNoHyphens) => {
    // Fetch and cache wind_data.csv rows for a location; reuse cache while the location is unchanged.

    try {
      let result = null;
      let rows = null;
      if (windAreaDataCache.location === location && windAreaDataCache.rows) {
        result = windAreaDataCache.rows;
    } else {
        const csvText = await fetchCsvText(`./data/${location}/wind/wind_data.csv`);
        if (csvText === null) {
          windAreaDataCache = {location, rows: []};
          addMessage(`Failed to load wind data for location: ${location}`);
          return [];
        }

        rows = parseCSV(csvText)
          .map(row => ({
            dateNoHyphens: parseWindDateToYYYYMMDD(row.time),
            lat: parseFloat(row.latitude),
            lon: parseFloat(row.longitude),
            u_wind: parseFloat(row.u_wind),
            v_wind: parseFloat(row.v_wind)
          }))
          .filter(row => row.dateNoHyphens && Number.isFinite(row.lat) && Number.isFinite(row.lon));
        result = rows;
      }
      // Also load ./data/${location}/wind/rss_wind_data_<date>.csv and merge it in with the results, if it exists. This allows for more recent wind data to be used without overwriting the main wind_data.csv file.
      const rssCsvText = await fetchCsvText(`./data/${location}/wind/rss_wind_data_${dateNoHyphens}.csv`);
      if (rssCsvText !== null) {
        const rssRows = parseCSV(rssCsvText)
          .map(row => ({
            dateNoHyphens: dateNoHyphens,
            lat: parseFloat(row.latitude),
            lon: parseFloat(row.longitude),
            u_wind: parseFloat(row.u_wind),
            v_wind: parseFloat(row.v_wind)
          }));
        result.push(...rssRows);
      } else {
        // Add message indicating that the RSS wind data for the specific date could not be loaded.
        addMessage(`No RSS wind data found for date: ${dateNoHyphens} at location: ${location}`);
      }

      if (rows) windAreaDataCache = {location, rows: rows};  // cache the main wind_data.csv rows for future use
      return result;
    } catch (error) {
      console.error('Error loading wind_data.csv:', error);
      windAreaDataCache = {location, rows: []};
      return [];
    }
  };

  const renderWindAreaArrowsForDate = (rows, dateNoHyphens) => {
    // Render area-wide wind arrows for one target date, centered exactly on each lat/lon point.
    windAreaSource.clear();
    if (!dateNoHyphens) {
      windAreaLayer.setVisible(false);
      return 0;
    }

    const matchingRows = rows.filter(row => row.dateNoHyphens === dateNoHyphens);
    matchingRows.forEach(row => {
      if (!Number.isFinite(row.u_wind) || !Number.isFinite(row.v_wind)) return;

      const windDirection = getWindDirection(row.u_wind, row.v_wind);
      const windMagnitude = Math.sqrt(row.u_wind * row.u_wind + row.v_wind * row.v_wind);
      const arrowFeatures = createArrowFeature([row.lon, row.lat], windDirection, windMagnitude, 0.03, 2, 0.0, 1.0, '#800080');
      if (arrowFeatures) {
        arrowFeatures.forEach(feature => {
          feature.setProperties({
            arrowType: 'wind',
            u_wind: row.u_wind,
            v_wind: row.v_wind,
            wind_speed_kmh: windMagnitude * 3.6
          });
          windAreaSource.addFeature(feature);
        });
      }
    });

    windAreaLayer.setVisible(matchingRows.length > 0);
    return matchingRows.length;
  };

  const loadWindAreaArrowsForDate = async ({dateValue, location} = {}) => {
    // Load/render area-wide wind arrows for an explicit date, e.g. from a point click.
    if (!show_grid_wind_arrows) {
      return;
    }

    if (!dateValue) {
      windAreaSource.clear();
      windAreaLayer.setVisible(false);
      return;
    }

    const dateNoHyphens = formatDateAsYYYYMMDD(dateValue);
    const rows = await ensureWindAreaDataForLocation(location, dateNoHyphens);
    renderWindAreaArrowsForDate(rows, dateNoHyphens);
  };

  /**
   * Single entry point keeping Clouds, Currents, Vorticity, and Wind arrows (plus Chl,
   * which derives its own nearest-file date from the same value) synchronized with one shared date.
   * @param {Date|null} dateValue Target date, typically the earliest displayed orbit date or a clicked point's date.
   * @param {Object} [options] Behavior options.
   * @param {boolean} [options.emitNoDateMessage=false] Emit a single combined message when no valid date is available.
   * @returns {Promise<void>} Resolves once all enabled overlays have been refreshed.
   */
  const refreshDateDependentOverlays = async (dateValue, {emitNoDateMessage = false} = {}) => {
    const isValidDate = dateValue instanceof Date && !Number.isNaN(dateValue.getTime());
    feature_display_date = isValidDate ? dateValue : null;

    if (show_clouds && isValidDate) {
      updateCloudLayerDate(dateValue);
    } else {
      cloudLayer.setVisible(false);
    }

    if (show_vorticity && isValidDate) {
      await showVorticityForDate({dateValue, location: show_location});
    } else {
      removeVorticityDisplay({emitMessage: false});
    }

    if (show_current_arrows && !show_vorticity && isValidDate) {
      await loadCurrentArrowsForDate({dateValue, location: show_location, emitNoDateMessage: false});
    } else if (!show_current_arrows && !show_vorticity) {
      currentSource.clear();
      updateCurrentArrowLayerVisibility();
    }

    if (show_grid_wind_arrows && isValidDate) {
      await loadWindAreaArrowsForDate({dateValue, location: show_location});
    } else {
      windAreaSource.clear();
      windAreaLayer.setVisible(false);
    }

    if (show_chl && isValidDate) {
      await refreshChlOverlayForDate(dateValue);
    } else {
      removeChlOverlay({emitMessage: false});
    }

    if (show_bga && isValidDate) {
      await refreshBgaOverlayForDate(dateValue);
    } else {
      removeBgaOverlay({emitMessage: false});
    }

    if (show_mblh && isValidDate) {
      await refreshMblhOverlayForDate(dateValue);
    } else {
      removeMblhOverlay();
    }

    if (!isValidDate && emitNoDateMessage && (show_clouds || show_vorticity || show_current_arrows || show_wind_arrows || show_grid_wind_arrows || show_chl || show_bga || show_mblh)) {
      addMessage('No displayed orbit is available for Clouds, Currents, Vorticity, Wind, Chl, or BGA');
    }

    updateSpectrumSummary();
  };
  
  // Function to load and display vorticity heatmap
  const loadVorticityHeatmap = async (lat, lon, soundingId, area, location) => {
    const clickedDate = parseDateFromSoundingId(soundingId);
    setVorticityCheckboxState(true);
    await showVorticityForDate({
      dateValue: clickedDate,
      location,
      areaFilter: (rowLat, rowLon) => Math.abs(rowLat - lat) <= area && Math.abs(rowLon - lon) <= area
    });

    // Keep popup behavior consistent with earlier interaction model.
    popupContainer.style.display = 'none';
    isPopupPersisted = false;
  };
  
  // Function to load and display data
  const loadData = async (showValue, locationValue, options = {}) => {
    try {
      const iffySuffix = use_iffy_filter_files ? '_exp' : '';
      let filePath;

      if (showValue === "delta_avg12" || showValue === "delta_avg25" || showValue === "delta_avg_25_max_min") {
        filePath = `./data/${locationValue}/data_gradient_averaged_${locationValue}${iffySuffix}.csv`;  // all data in this file
      } else if (showValue === "delta") {
        filePath = `./data/${locationValue}/data_gradient_raw_${locationValue}${iffySuffix}.csv`;  // all data in this file  
      } else if (showValue === "raw_avg12" || showValue === "raw_avg25" || showValue === "co2_max_min") {
        filePath = `./data/${locationValue}/data_averaged_${locationValue}${iffySuffix}.csv`;  // all data in this file
      } else {
        filePath = `./data/${locationValue}/data_${showValue}_${locationValue}${iffySuffix}.csv`;
      }
      updateMessage(`Loading data_${filePath}...`);

      const csvText = await fetchCsvText(filePath);

      if (csvText === null) {
        if (iffySuffix) {
          updateMessage(`Error: The "_exp" version of the data is not available for this location`);
        } else {
          updateMessage(`Error: Could not load file ${filePath}`);
        }
        return;
      }

      console.log(`Loaded CSV data from ${filePath}, length: ${csvText.length} characters ${csvText.slice(0, 50)} ...`);
      const data = parseCSV(csvText);
      const parsedData = data.map((row, index) => ({...row, _date: parseDateFromRow(row), _index: index}));
      const validDates = parsedData
        .map(row => row._date)
        .filter(date => date instanceof Date && !Number.isNaN(date.getTime()));

      let earliestTime = Number.POSITIVE_INFINITY;
      let latestTime = Number.NEGATIVE_INFINITY;
      validDates.forEach(date => {
        const time = date.getTime();
        earliestTime = Math.min(earliestTime, time);
        latestTime = Math.max(latestTime, time);
      });

      date_range_min = Number.isFinite(earliestTime) ? new Date(earliestTime) : null;
      date_range_max = Number.isFinite(latestTime) ? new Date(latestTime) : null;

      if (date_range_min && date_range_max) {
        const span = Math.max(0, diffDays(date_range_min, date_range_max));
        const hasExistingRange = date_range_start && date_range_end && date_range_start >= date_range_min && date_range_end <= date_range_max;

        if (!hasExistingRange) {
          date_range_start = new Date(date_range_min);
        } else {
          date_range_start = new Date(Math.max(date_range_min.getTime(), Math.min(date_range_start.getTime(), date_range_max.getTime())));
        }

        const effectiveWindow = Math.min(date_range_window, span);
        date_range_end = addDays(date_range_start, effectiveWindow);
        if (date_range_end > date_range_max) {
          date_range_end = new Date(date_range_max);
        }
      } else {
        date_range_start = null;
        date_range_end = null;
      }

      controlBand.updateDateRangeControls({
        minDate: date_range_min,
        maxDate: date_range_max,
        startDate: date_range_start,
        endDate: date_range_end,
        windowSize: date_range_window,
        disabled: false
      });

      // First pass: apply non-value filters so slider bounds are based on the active dataset.
      const rowsBeforeValueFilter = parsedData.filter(row => {
        if (!row._date) return false;
        if (exclude_edges && show_selection === "raw_avg25" && row.avg_25_point_count < 8) {
          return false; // Too few points for an average
        }
        if (exclude_edges && show_selection === "raw_avg12" && row.avg_12_point_count < 10) {
          return false; // Too few points for an average
        }
        if (exclude_edges && (show_selection === "delta_avg25" ) && row.avg_25_count < 8) {
          return false; // Too few points for an average
        }
        if (exclude_edges && (show_selection === "delta_avg12" ) && row.avg_12_count < 4) {
          return false; // Too few points for an average
        }
        if (exclude_edges && (show_selection === "co2_max_min") && row.avg_25_point_count < 8 ) {
          return false; // Too few points for an average
        }
        // if (show_selection === "co2_max_min" && !(row.in_top_10_percent === "1"  || row.in_bottom_10_percent === "1")) {
        if (show_selection === "co2_max_min" && !(row.peaks === "1"  || row.valleys === "1")) {
          return false; // Not in top or bottom 10%
        }
        // if (show_selection === "delta_avg_25_max_min" && !(row.in_top_10_percent === "1"  || row.in_bottom_10_percent === "1")) {
        if (show_selection === "delta_avg_25_max_min" && !(row.peaks === "1"  || row.valleys === "1")) {
          return false; // Not in top or bottom 10%
        }
        if (exclude_edges && (show_selection === "delta_avg_25_max_min") && row.avg_25_count < 8 ) {
          return false; // Too few points for an average
        }
        if (!show_land_points && Object.prototype.hasOwnProperty.call(row, 'land_water_indicator')) {
          // Step 1: optionally remove land-only points.
          const landWater = toFiniteNumber(row.land_water_indicator);
          if (landWater === 0) {
            return false;
          }
        }

        // Step 2: apply OCO2/OCO3/SOCAT filters using satellite_id when present.
        if (Object.prototype.hasOwnProperty.call(row, 'satellite_id')) {
          const satelliteId = toFiniteNumber(row.satellite_id);
          if (!show_oco2 && satelliteId == 2) {
            return false;
          }
          if (!show_oco3 && satelliteId == 3) {
            return false;
          }
          if (!show_socat && satelliteId == 4) {
            return false;
          }
        }
        return row._date >= date_range_start && row._date <= date_range_end;
      });

      const fullMetricRange = computeMetricRange(rowsBeforeValueFilter, show_selection);
      const filterGroup = getValueFilterGroup(show_selection);
      const shouldResetValueRange = Boolean(options.resetValueFilter);

      if (options.clearRememberedValueFilters) {
        remembered_value_filter_ranges.co2 = null;
        remembered_value_filter_ranges.gradient = null;
      }

      if (!fullMetricRange) {
        value_filter_absolute_min = null;
        value_filter_absolute_max = null;
        value_filter_min = null;
        value_filter_max = null;
      } else {
        value_filter_absolute_min = fullMetricRange.min;
        value_filter_absolute_max = fullMetricRange.max;
        value_filter_unit = fullMetricRange.unit;
        value_filter_decimals = fullMetricRange.decimals;

        let remembered = remembered_value_filter_ranges[filterGroup];
        if (shouldResetValueRange || !remembered) {
          remembered = {
            min: fullMetricRange.min,
            max: fullMetricRange.max,
            isSticky: true
          };
        }

        if (remembered.isSticky) {
          value_filter_min = fullMetricRange.min;
          value_filter_max = fullMetricRange.max;
          remembered_value_filter_ranges[filterGroup] = {
            min: value_filter_min,
            max: value_filter_max,
            isSticky: true
          };
        } else {
          const clampedMin = Math.max(fullMetricRange.min, Math.min(remembered.min, fullMetricRange.max));
          const clampedMax = Math.max(fullMetricRange.min, Math.min(remembered.max, fullMetricRange.max));
          value_filter_min = Math.min(clampedMin, clampedMax);
          value_filter_max = Math.max(clampedMin, clampedMax);
          remembered_value_filter_ranges[filterGroup] = {
            min: remembered.min,
            max: remembered.max,
            isSticky: false
          };
        }
      }

      controlBand.updateValueRangeControls({
        min: value_filter_absolute_min,
        max: value_filter_absolute_max,
        selectedMin: value_filter_min,
        selectedMax: value_filter_max,
        unit: value_filter_unit,
        decimals: value_filter_decimals,
        disabled: !fullMetricRange
      });

      // All rows remain displayed regardless of the value filter; out-of-range points are colored gray
      // with a -/+ marker instead of being removed. The in-range subset below only drives the color spectrum.
      const rowsToDisplay = rowsBeforeValueFilter;
      const rowsInValueRange = rowsBeforeValueFilter.filter(row => {
        if (!fullMetricRange || value_filter_min === null || value_filter_max === null) {
          return true;
        }
        const value = getRowMetricValue(row, show_selection);
        if (value === null) {
          return true;
        }
        return value >= value_filter_min && value <= value_filter_max;
      });

      // Track the earliest orbit date currently on screen for checkbox-driven vorticity selection.
      earliest_displayed_orbit_date = null;
      rowsToDisplay.forEach(row => {
        if (!row._date) return;
        if (!earliest_displayed_orbit_date || row._date < earliest_displayed_orbit_date) {
          earliest_displayed_orbit_date = row._date;
        }
      });

      // Apply the color spectrum to the in-range subset, not the full pre-filter range.
      const filteredMetricRange = computeMetricRange(rowsInValueRange, show_selection) || fullMetricRange;
      
      // Clear existing features
      vectorSource.clear();

      // Lookup from original row index to lon/lat, used to draw valley-width lines that may reference
      // points outside the currently displayed/filtered subset.
      const pointByIndex = new globalThis.Map();
      parsedData.forEach(row => {
        const lon = toFiniteNumber(row.longitude);
        const lat = toFiniteNumber(row.latitude);
        if (lon !== null && lat !== null) {
          pointByIndex.set(row._index, [lon, lat]);
        }
      });

      let minLon = Infinity, maxLon = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;
      let rownum = 0;

      // Add color spectrum showing colors for min and max xcos2 or co2_derivative values
      const spectrumContainer = document.getElementById('color-spectrum');
      spectrumContainer.innerHTML = ''; // Clear existing spectrum

      const spectrumBarHost = document.createElement('div');
      spectrumBarHost.id = 'main-spectrum-bar';
      spectrumBarHost.style.position = 'relative';
      spectrumBarHost.style.width = '100%';
      spectrumBarHost.style.height = '20px';
      spectrumBarHost.style.display = show_split_color_spectrum ? 'flex' : 'block';
      spectrumContainer.appendChild(spectrumBarHost);

      if (show_split_color_spectrum) {
        const spectrumBarL = document.createElement('div');
        spectrumBarL.style.width = '49%';
        spectrumBarL.style.height = '20px';
        // spectrumBarL.style.background = 'linear-gradient(in hsl to left, hsl(0, 100%, 50%), hsl(50, 100%, 50%), hsl(100, 100%, 50%), hsl(150, 100%, 50%), hsl(200, 100%, 50%), hsl(240, 100%, 50%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        // spectrumBarL.style.background = 'linear-gradient(in hsl to left, hsl(0, 100%, 50%), hsl(40, 100%, 50%), hsl(80, 100%, 50%), hsl(180, 10０%, 5０%), hsl(2００, 1００%, 5０%), hsl(2２０, 1００%, 5０%), hsl(2４０, 1００%, 5０%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        // spectrumBarL.style.background = 'linear-gradient(in hsl to left, hsl(190, 100%, 50%), hsl(260, 100%, 50%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        spectrumBarL.style.background = 'linear-gradient(in hsl to left, hsl(240, 100%, 95%), hsl(240, 100%, 25%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        spectrumBarL.style.display = 'inline-block';
        spectrumBarHost.appendChild(spectrumBarL);

        const spectrumBarM = document.createElement('div');
        spectrumBarM.style.width = '.1px';
        spectrumBarM.style.height = '20px';
        spectrumBarM.style.background = 'linear-gradient(in hsl to left, hsl(175, 0%, 100%), hsl(177, 0%, 100%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        spectrumBarM.style.display = 'inline-block';
        spectrumBarHost.appendChild(spectrumBarM);

        const spectrumBarR = document.createElement('div');
        spectrumBarR.style.width = '49%';
        spectrumBarR.style.height = '20px';
        // spectrumBarR.style.background = 'linear-gradient(in hsl to left, hsl(0, 100%, 50%), hsl(60, 100%, 50%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        spectrumBarR.style.background = 'linear-gradient(in hsl to left, hsl(10, 100%, 20%), hsl(10, 100%, 90%))'; // `linear-gradient(to right, ${minColor}, ${maxColor})`;
        spectrumBarR.style.display = 'inline-block';
        spectrumBarHost.appendChild(spectrumBarR);
      } else {
        const spectrumBar = document.createElement('div');

        spectrumBar.style.width = '100%';
        spectrumBar.style.height = '20px';
        // spectrumBar.style.background = `linear-gradient(to left, #ff0000, #ffa500, #ffff00, #008000, #0000ff, #4b0082)`;
        // spectrumBar.style.background = `linear-gradient(to left, #ffb3ba, #ffdfba, #ffffba, #baffc9, #bae1ff, #e8baff)`;
        spectrumBar.style.background = `linear-gradient(to left,hsl(0, 100%, 50%),   hsl(30, 100%, 50%),  hsl(60, 100%, 50%), 
        hsl(90, 100%, 50%),  hsl(120, 100%, 50%), hsl(150, 100%, 50%), 
        hsl(180, 100%, 50%), hsl(210, 100%, 50%), hsl(240, 100%, 50%), 
        hsl(270, 100%, 50%), hsl(300, 100%, 50%))`;
        spectrumBarHost.appendChild(spectrumBar);
      }

      ensureMainSpectrumMarker();
      clearMainSpectrumIndicator();
      current_spectrum_metric_range = filteredMetricRange;

      const labels = document.createElement('div');
      labels.style.display = 'flex';
      labels.style.justifyContent = 'space-between';

      if (filteredMetricRange) {
        const midpoint = filteredMetricRange.midpoint === 'zero'
          ? '0'
          : ((filteredMetricRange.min + filteredMetricRange.max) / 2).toFixed(filteredMetricRange.decimals);
        labels.innerHTML = `<span>${formatMetricValue(filteredMetricRange.min, filteredMetricRange.decimals, filteredMetricRange.unit)}</span><span>${midpoint}</span><span>${formatMetricValue(filteredMetricRange.max, filteredMetricRange.decimals, filteredMetricRange.unit)}</span>`;
      } else {
        labels.innerHTML = '<span>N/A</span><span>N/A</span><span>N/A</span>';
      }
      spectrumContainer.appendChild(labels);

      const hoverValue = document.createElement('div');
      hoverValue.id = 'main-spectrum-hover-value';
      hoverValue.style.display = 'flex';
      hoverValue.style.justifyContent = 'center';
      hoverValue.style.marginTop = '2px';
      hoverValue.style.minHeight = '1.2em';
      hoverValue.innerHTML = '&nbsp;';
      spectrumContainer.appendChild(hoverValue);

      // remember last arrow location so we don't make them too close together
      let lastArrowLon = null;
      let lastArrowLat = null;

      rowsToDisplay.forEach(row => {
        if (row.latitude && row.longitude && row.xco2) {
          rownum+= 1;
          if (rownum % 1000 === 0) {
            console.log(`Processing row ${rownum} - Lon: ${row.longitude}, Lat: ${row.latitude}, xCO2: ${row.xco2}`);
          }
          const lon = parseFloat(row.longitude);
          const lat = parseFloat(row.latitude);
          
          minLon = Math.min(minLon, lon);
          maxLon = Math.max(maxLon, lon);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);

          // Create feature
          const feature = new Feature({
            geometry: new Point(fromLonLat([lon, lat])),
            isGraphDataPoint: true,
            ...row
          });
          let color = 'rgba(255, 255, 255, 0.6)'; // Default gray for missing values
          const metricValue = getRowMetricValue(row, show_selection);
          const belowValueFilter = metricValue !== null && Number.isFinite(value_filter_min) && metricValue < value_filter_min;
          const aboveValueFilter = metricValue !== null && Number.isFinite(value_filter_max) && metricValue > value_filter_max;
          if (belowValueFilter || aboveValueFilter) {
            color = 'rgba(235, 235, 235, 0.3)'; // Light gray for values outside the Value filter range
          } else if (metricValue !== null && filteredMetricRange) {
            color = getColorForValue(metricValue, filteredMetricRange.scale, filteredMetricRange.min, filteredMetricRange.max, show_split_color_spectrum);
          }
          const rangeMarkSymbol = belowValueFilter ? '-' : (aboveValueFilter ? '+' : null);
          const cloudMarkSymbol = rangeMarkSymbol ? null : getCloudMarkSymbol(row, show_selection);
          const featureStyles = [new Style({
            image: new Circle({
              fill: new Fill({color: color}),
              stroke: new Stroke({color: '#333', width: .1}),
              radius: 6,
              zIndex: 5000,
            }),
          })];

          if (rangeMarkSymbol) {
            featureStyles.push(new Style({
              text: new Text({
                text: rangeMarkSymbol,
                font: 'bold 18px Arial',
                // fill: new Fill({color: '#333'}),
                textAlign: 'center',
                // stroke: new Stroke({
                //   color: '#ffffff', // outline to make it pop over the gray fill
                //   width: 1.5
                // }), 
                offsetY: 1
              })
            }));
          } else if (cloudMarkSymbol) {
            featureStyles.push(new Style({
              text: new Text({
                text: cloudMarkSymbol,
                font: cloudMarkSymbol === 'X' ? 'bold 10px Arial' : 'bold 11px Arial',
                fill: new Fill({color: '#fff'}),
                textAlign: 'center',
                stroke: new Stroke({
                  color: '#000000', // outline to make it pop more
                  width: 1.5
                }), 
                offsetY: 1
              })
            }));
          }

          feature.setStyle(featureStyles);
          
          vectorSource.addFeature(feature);

          const valleyBounds = getValleyBounds(row);
          const isValidValley = valleyBounds !== null;
          const isValidPeak = row.peaks === '1' && toFiniteNumber(row.peak_width) > 5;
          // The Peaks/Valleys checkboxes independently gate the Maximum/Minimum icons.
          const extremaIconFile = (show_peaks && isValidPeak)
            ? 'Maximum.png'
            : ((show_valleys && isValidValley) ? 'Minimum.png' : null);
          if (extremaIconFile) {
            const extremaFeature = new Feature({
              geometry: new Point(fromLonLat([lon, lat])),
              isExtremaIcon: true,
              ...row
            });
            extremaFeature.setStyle(new Style({
              image: new Icon({
                src: `/${extremaIconFile}`,
                scale: 0.3,
                anchor: [1, 0.5],
                displacement: [-8, 0]  // -8 is distance from the original point to the left
              })
            }));
            vectorSource.addFeature(extremaFeature);
          }

          if (isValidValley && show_valleys) {
            // Draw a line spanning the valley when it's wide enough, offset/z-ordered to sit under the marker.
            // Gated by the Valleys checkbox, same as the Minimum.png icon above.
            const leftPoint = pointByIndex.get(valleyBounds.leftIndex);
            const rightPoint = pointByIndex.get(valleyBounds.rightIndex);
            if (leftPoint && rightPoint) {
              const valleyLineGeometry = new LineString([fromLonLat(leftPoint), fromLonLat(rightPoint)]);
              const valleyLineFeature = new Feature({
                geometry: valleyLineGeometry,
                isValleyWidthLine: true,
                zIndex: -1
              });
              valleyLineFeature.setStyle((_feature, resolution) => new Style({
                // Match the marker's [-8, 0] pixel displacement, converted to map units for the current resolution.
                geometry: valleyLineGeometry.clone().translate(-8 * resolution, 0),
                stroke: new Stroke({color: '#000000', width: 26}), // #ffed72 Highlighter color. (still hard to see)
                zIndex: -1,
                opacity: 0.2,
              }));
              vectorSource.addFeature(valleyLineFeature);
            }
          }

          if (show_sla) {
            const slaIconFile = getSLAIconFile(row.sla);
            if (slaIconFile) {
              // Create SLA icon feature at same location, offset 30px to the left
              const slaFeature = new Feature({
                geometry: new Point(fromLonLat([lon, lat])),
                ...row
              });
              
              slaFeature.setStyle(new Style({
                image: new Icon({
                  src: `/${slaIconFile}`,
                  scale: 0.8,
                  anchor: [0.5, 0.5],
                  displacement: [-20, 0] // 25px to the left, independent of zoom level
                })
              }));
              
              vectorSource.addFeature(slaFeature);
            }
          }

          // getDistance function to calculate distance between two coordinates in degrees (approximate, not accounting for Earth's curvature)
          function getDistance(coord1, coord2) {
            const [lon1, lat1] = coord1;
            const [lon2, lat2] = coord2;
            const dLon = lon2 - lon1;
            const dLat = lat2 - lat1;
            return Math.sqrt(dLon * dLon + dLat * dLat);
          }

          if (show_wind_arrows) {
            // Check distance from last arrow
            if (lastArrowLon == null || 
                lastArrowLon !== null && lastArrowLat !== null &&
                getDistance([lon, lat], [lastArrowLon, lastArrowLat]) > 0.5) {
                // Create arrow in wind direction
                const windDirection = getWindDirection(row.u_wind, row.v_wind);
                const windMagnitude = Math.sqrt(
                  Math.pow(parseFloat(row.u_wind) || 0, 2) + Math.pow(parseFloat(row.v_wind) || 0, 2)
                );
                const arrowFeatures = createArrowFeature([lon, lat], windDirection, windMagnitude, 0.03, 6);
                arrowFeatures.forEach(feature => {
                  feature.setProperties(row);
                  feature.setProperties({
                    arrowType: 'wind',
                    u_wind: parseFloat(row.u_wind),
                    v_wind: parseFloat(row.v_wind),
                    wind_speed_kmh: windMagnitude * 3.6
                  });
                  vectorSource.addFeature(feature);
                });
                lastArrowLon = lon;
                lastArrowLat = lat;
            }
          }
        }
      });
      
      // Generate and add orbit labels if enabled
      labelLayer.setVisible(show_orbit_labels);
      labelSource.clear();
      if (show_orbit_labels) {
        const labelPoints = getOrbitLabelPoints(rowsToDisplay);
        const labelFeatures = createLabelFeatures(labelPoints);
        labelFeatures.forEach(feature => {
          labelSource.addFeature(feature);
        });
      }
      
      // Center map on data bounds
      //   This code doesn't seem to work and it results in a blank screen.
      // if (minLon !== Infinity) {
      //   const center = fromLonLat([
      //     (minLon + maxLon) / 2,
      //     (minLat + maxLat) / 2
      //   ]);
      //   map.getView().setCenter(center);
        
      //   // Set zoom level based on bounds
      //   const extent = [
      //     fromLonLat([minLon, minLat]),
      //     fromLonLat([maxLon, maxLat])
      //   ];
      //   map.getView().fit(extent, {padding: [50, 50, 50, 50]});
         console.log(`Data bounds - Lon: [${minLon}, ${maxLon}], Lat: [${minLat}, ${maxLat}]`);
      // }
      
      addMessage(`Loaded ${rownum} data points from ${locationValue} ${showValue}<br><br>Shift-Click to create a marked point.`);
      last_loaded_location = locationValue;
      updateSpectrumSummary();

      // Keep the shared date used by Clouds/Currents/Vorticity/Wind/Chl valid for the reloaded dataset:
      // keep it if it's still within the displayed date range, otherwise move it to the earliest displayed orbit.
      const featureDateStillInRange = feature_display_date instanceof Date && !Number.isNaN(feature_display_date.getTime())
        && date_range_start && date_range_end
        && feature_display_date >= date_range_start && feature_display_date <= date_range_end;
      let nextFeatureDate = featureDateStillInRange ? feature_display_date : earliest_displayed_orbit_date;
      if (!featureDateStillInRange && nextFeatureDate && date_range_end) {
        const dayAfterEarliest = addDays(nextFeatureDate, 1);
        if (dayAfterEarliest <= date_range_end) nextFeatureDate = dayAfterEarliest;
      }
      await refreshDateDependentOverlays(nextFeatureDate, {emitNoDateMessage: false});
      syncUrlWithCurrentState();
    } catch (error) {
      console.error('Error loading CSV:', error);
      updateMessage(`Error: ${error.message}`);
    }
  };
  
  // Wire up Show dropdown
  controlBand.showDropdown.addEventListener('change', (e) => {
    show_selection = e.target.value;
    syncUrlWithCurrentState();
  });
  
  // Wire up Location dropdown
  controlBand.locationDropdown.addEventListener('change', (e) => {
    show_location = e.target.value;
    syncUrlWithCurrentState();
  });

  controlBand.dateRangeSlider.addEventListener('input', (e) => {
    if (!date_range_min || !date_range_max) return;

    const startOffset = parseInt(e.target.value, 10);
    date_range_start = addDays(date_range_min, startOffset);
    const effectiveWindow = Math.min(date_range_window, Math.max(0, diffDays(date_range_min, date_range_max)));
    date_range_end = addDays(date_range_start, effectiveWindow);
    if (date_range_end > date_range_max) {
      date_range_end = new Date(date_range_max);
    }

    controlBand.updateDateRangeControls({
      minDate: date_range_min,
      maxDate: date_range_max,
      startDate: date_range_start,
      endDate: date_range_end,
      windowSize: date_range_window,
      disabled: false
    });
    loadData(show_selection, show_location);
    syncUrlWithCurrentState();
  });

  controlBand.windowSizeSlider.addEventListener('input', (e) => {
    date_range_window = parseInt(e.target.value, 10);
    if (!date_range_min || !date_range_max || !date_range_start) return;

    const effectiveWindow = Math.min(date_range_window, Math.max(0, diffDays(date_range_min, date_range_max)));
    date_range_end = addDays(date_range_start, effectiveWindow);
    if (date_range_end > date_range_max) {
      date_range_end = new Date(date_range_max);
    }

    controlBand.updateDateRangeControls({
      minDate: date_range_min,
      maxDate: date_range_max,
      startDate: date_range_start,
      endDate: date_range_end,
      windowSize: date_range_window,
      disabled: false
    });
    loadData(show_selection, show_location);
    syncUrlWithCurrentState();
  });

  // Re-filter data as either value-filter handle moves.
  const applyValueRangeAndReload = () => {
    if (value_filter_absolute_min === null || value_filter_absolute_max === null) {
      return;
    }
    const selectedRange = controlBand.getValueRangeSelection();
    const filterGroup = getValueFilterGroup(show_selection);

    const sliderStep = (value_filter_absolute_max - value_filter_absolute_min) / 1000;
    const tolerance = Math.max(1e-5, sliderStep * 1.5);
    const isAtExtremeMin = Math.abs(selectedRange.min - value_filter_absolute_min) <= tolerance || selectedRange.min <= value_filter_absolute_min;
    const isAtExtremeMax = Math.abs(selectedRange.max - value_filter_absolute_max) <= tolerance || selectedRange.max >= value_filter_absolute_max;
    const isSticky = isAtExtremeMin && isAtExtremeMax;

    if (isSticky) {
      value_filter_min = value_filter_absolute_min;
      value_filter_max = value_filter_absolute_max;
      remembered_value_filter_ranges[filterGroup] = {
        min: value_filter_absolute_min,
        max: value_filter_absolute_max,
        isSticky: true
      };
    } else {
      value_filter_min = selectedRange.min;
      value_filter_max = selectedRange.max;
      remembered_value_filter_ranges[filterGroup] = {
        min: value_filter_min,
        max: value_filter_max,
        isSticky: false
      };
    }

    loadData(show_selection, show_location);
    syncUrlWithCurrentState();
  };

  controlBand.valueRangeMinSlider.addEventListener('input', applyValueRangeAndReload);
  controlBand.valueRangeMaxSlider.addEventListener('input', applyValueRangeAndReload);
  
  // Wire up checkboxes
  controlBand.checkboxes.forEach(cb => {
    cb.element.addEventListener('change', async (e) => {
      if (cb.variable === 'show_chl') show_chl = e.target.checked;
      else if (cb.variable === 'show_sla') show_sla = e.target.checked;
      else if (cb.variable === 'show_wind_arrows') show_wind_arrows = e.target.checked;
      else if (cb.variable === 'show_grid_wind_arrows') show_grid_wind_arrows = e.target.checked;
      else if (cb.variable === 'exclude_edges') exclude_edges = e.target.checked;
      else if (cb.variable === 'mark_iffy_points') mark_iffy_points = e.target.checked;
      else if (cb.variable === 'show_land_points') show_land_points = e.target.checked;
      else if (cb.variable === 'show_oco2') show_oco2 = e.target.checked;
      else if (cb.variable === 'show_oco3') show_oco3 = e.target.checked;
      else if (cb.variable === 'show_socat') show_socat = e.target.checked;
      else if (cb.variable === 'show_vorticity') show_vorticity = e.target.checked;
      else if (cb.variable === 'show_clouds') show_clouds = e.target.checked;
      else if (cb.variable === 'show_current_arrows') show_current_arrows = e.target.checked;
      else if (cb.variable === 'show_bga') show_bga = e.target.checked;
      else if (cb.variable === 'show_mblh') show_mblh = e.target.checked;
      else if (cb.variable === 'show_peaks') show_peaks = e.target.checked;
      else if (cb.variable === 'show_valleys') show_valleys = e.target.checked;
      else if (cb.variable === 'use_iffy_filter_files') use_iffy_filter_files = e.target.checked;
      else if (cb.variable === 'show_orbit_labels') {
        show_orbit_labels = e.target.checked;
        labelLayer.setVisible(show_orbit_labels);
      }
      else if (cb.variable === 'show_split_color_spectrum') show_split_color_spectrum = e.target.checked;
      else if (cb.variable === 'show_satellite_view') {
        show_satellite_view = e.target.checked;
        if (show_satellite_view) {
          if (!google_map_satellite_source) {
            google_map_satellite_source =  create_google_satellite_source (key)
          };
          background_tile_layer.setSource(google_map_satellite_source);
        } else {
          if (!google_map_roadmap_source) { 
            google_map_roadmap_source = create_google_roadmap_source(key);
          }
          background_tile_layer.setSource(google_map_roadmap_source);
        }
      }

      // Toggles that change the underlying data selection require a full CSV reload; loadData's
      // tail keeps Clouds/Currents/Vorticity/Wind/Chl synchronized with the active shared date.
      const requiresDataReload = cb.variable === 'show_chl' || cb.variable === 'show_sla' || cb.variable === 'show_wind_arrows'
        || cb.variable === 'show_split_color_spectrum' || cb.variable === 'exclude_edges'
        || cb.variable === 'mark_iffy_points'
        || cb.variable === 'show_land_points' || cb.variable === 'show_oco2' || cb.variable === 'show_oco3' || cb.variable === 'show_socat' || cb.variable === 'show_bga'
        || cb.variable === 'show_peaks' || cb.variable === 'show_valleys' || cb.variable === 'use_iffy_filter_files';

      if (requiresDataReload && cb.variable !== 'show_bga') {
        await loadData(show_selection, show_location);
      } else if (cb.variable === 'show_bga') {
        await refreshDateDependentOverlays(feature_display_date || earliest_displayed_orbit_date, {emitNoDateMessage: true});
      } else if (cb.variable === 'show_clouds' || cb.variable === 'show_vorticity' || cb.variable === 'show_current_arrows' || cb.variable === 'show_grid_wind_arrows' || cb.variable === 'show_mblh') {
        await refreshDateDependentOverlays(feature_display_date || earliest_displayed_orbit_date, {emitNoDateMessage: true});
      }

      if (cb.variable === 'exclude_edges') {
        updateSpectrumSummary();
      }
      syncUrlWithCurrentState();
    });
  });
  
  // Wire up Load Data button
  controlBand.loadButton.addEventListener('click', () => {
    const locationChanged = show_location !== last_loaded_location;
    const loadOptions = locationChanged
      ? { resetValueFilter: true, clearRememberedValueFilters: true }
      : {};
    loadData(show_selection, show_location, loadOptions);
    syncUrlWithCurrentState();
  });

  controlBand.measureToolButton?.addEventListener('click', () => {
    if (!measureTool) return;
    if (measureTool.isActive()) {
      measureTool.cancel();
      return;
    }
    measureTool.activate();
  });

  controlBand.copyLocationButton?.addEventListener('click', async () => {
    try {
      const bookmarkUrl = buildBookmarkUrl();
      await copyTextToClipboard(bookmarkUrl);
      setCopyStatus('URL copied to clipboard');
    } catch (error) {
      console.error('Copy to clipboard failed:', error);
      setCopyStatus('Copy failed');
    }
  });

  map.on('moveend', () => {
    syncUrlWithCurrentState();
  });
  
  // Discover available files and populate location dropdown
  (async () => {
    try {
      const  availableLocations = await scanForDataFiles();
      // console.log('Available locations:', availableLocations);

      // Update location dropdown
      if (availableLocations.length > 0) {
        controlBand.locationDropdown.innerHTML = '';
        availableLocations.forEach(location => {
          const option = document.createElement('option');
          option.value = location;
          option.textContent = location.charAt(0).toUpperCase() + location.slice(1);
          if (location === show_location) option.selected = true;
          controlBand.locationDropdown.appendChild(option);
        });
      }

      defaultLocationForUrl = show_location;

      if (startupQueryConfig?.hasAnyParams) {
        // Step 1: apply checkbox states without triggering change handlers.
        suppressUrlSync = true;
        Object.keys(URL_BOOLEAN_PARAMS).forEach(paramName => {
          if (!Object.prototype.hasOwnProperty.call(startupQueryConfig.checkboxes, paramName)) return;
          const variableName = URL_BOOLEAN_PARAMS[paramName];
          setStartupCheckboxState(variableName, startupQueryConfig.checkboxes[paramName]);
        });

        labelLayer.setVisible(show_orbit_labels);
        cloudLayer.setVisible(show_clouds);
        updateCurrentArrowLayerVisibility();

        // Step 2: apply show value if valid.
        if (startupQueryConfig.show) {
          const showFromValue = Object.values(showTagMap).includes(startupQueryConfig.show)
            ? startupQueryConfig.show
            : showTagMap[startupQueryConfig.show];
          if (showFromValue) {
            show_selection = showFromValue;
            controlBand.showDropdown.value = showFromValue;
          } else {
            addIgnoredStartupNote('show');
          }
        }

        // Step 3: apply location if valid and load data.
        if (startupQueryConfig.location) {
          if (availableLocations.includes(startupQueryConfig.location)) {
            show_location = startupQueryConfig.location;
            controlBand.locationDropdown.value = startupQueryConfig.location;
          } else {
            addIgnoredStartupNote('location');
          }
        }

        await loadData(show_selection, show_location, { resetValueFilter: true });

        // Step 4: apply deferred date and value filter parameters after location data is loaded.
        await applyStartupDateAndValueParams();

        // Step 5: add up to 10 URL points.
        startupQueryConfig.points.slice(0, URL_MAX_POINTS).forEach(point => {
          addClickedPoint([point.lon, point.lat], fromLonLat([point.lon, point.lat]));
        });

        // Step 6: add up to 10 validated measurement lines from URL state.
        measureTool?.loadLinesFromQuery(startupQueryConfig.lines);

        // Step 7: apply map center and zoom.
        if (startupQueryConfig.centerRequested) {
          if (startupQueryConfig.center) {
            map.getView().setCenter(fromLonLat([startupQueryConfig.center.lon, startupQueryConfig.center.lat]));
          } else {
            addIgnoredStartupNote('center');
          }
        }
        if (startupQueryConfig.zoomRequested) {
          if (Number.isFinite(startupQueryConfig.zoom)) {
            map.getView().setZoom(startupQueryConfig.zoom);
          } else {
            addIgnoredStartupNote('zoom');
          }
        }

        if (startupIgnoredParams.size > 0) {
          addMessage(`Ignored URL parameter(s): ${Array.from(startupIgnoredParams).sort().join(', ')}`);
        }

        suppressUrlSync = false;
        syncUrlWithCurrentState();
      } else {
        // No URL params supplied: load default data and sync URL with defaults omitted.
        await loadData(show_selection, show_location, { resetValueFilter: true });
        syncUrlWithCurrentState();
      }
    } catch (e) {
      console.error('Error discovering data files:', e);
      updateMessage('Error discovering data files');
    }
  })();

  // Helper function to show popup for a data feature
  const showPopupForFeature = (feature, coordinate, fromClick = false) => {
    const props = feature.getProperties();
    const sounding_id = props.sounding_id || 'N/A';
    // Step 1: format popup timestamp in the local timezone for the data point coordinates.
    const timestamp = formatLocalTimestampForPoint(sounding_id, props.latitude, props.longitude);
    let popupHTML = `<div style="font-weight: bold;">${timestamp}</div>`;

    const supplementalValues = getPopupSupplementalValuesForFeature(feature, {requireDateMatch: !fromClick});

    // Note: the shared Clouds/Currents/Vorticity/Wind/Chl date is already moved by the caller
    // (via refreshDateDependentOverlays) before this popup is shown for a click.
    
    // Helper function to format a field value
    const formatField = (key, value) => {
      let displayKey = key.replace(/_/g, ' ').toUpperCase();
      if (key === 'co2_derivative') {
        return `<div><strong>CO2 GRADIENT:</strong> ${parseFloat(value).toFixed(2)}</div>`;
      } else if (key === 'gradient_avg_12') {
        return `<div><strong>CO2 GRADIENT AVG:</strong> ${parseFloat(value).toFixed(2)}</div>`;
      } else if (key === 'gradient_avg_25') {
        return `<div><strong>CO2 GRADIENT AVG 25:</strong> ${parseFloat(value).toFixed(2)}</div>`;
      } else if (key === 'xco2_std_15km') {
        return `<div><strong>STD 15km:</strong> ${parseFloat(value).toFixed(3)}</div>`;
      } else if (key === 'gradient_std_12') {
        return `<div><strong>STD 12KM:</strong> ${parseFloat(value).toFixed(3)}</div>`;
      } else if (key === 'latitude') {
        // Location is displayed for latitude.  Shouldn't call also for longitude, but if we do, it will be ignored.
        return `<div><strong>LOC:</strong> ${parseFloat(value).toFixed(3)}, ${parseFloat(props['longitude']).toFixed(3)}</div>`;
      } else if (displayKey === 'U WIND') {
        const uVal = parseFloat(value);
        const vVal = parseFloat(props['v_wind']);
        if (isNaN(uVal) || isNaN(vVal)) {
          return '';
        }
        return `<div><strong>Wind:</strong> ${(uVal * 3600 / 1000).toFixed(1)} E, ${(vVal * 3600 / 1000).toFixed(1)} N km/h</div>`;
      } else if (displayKey === 'V WIND') {
        return '';  // V wind is handled together with U wind, so we don't show it separately.
      } else if (displayKey === 'XCO2' || displayKey === 'XCO2 AVG 12' || displayKey === 'XCO2 AVG 25') {
        return `<div><strong>${displayKey}:</strong> ${(parseFloat(value)).toFixed(2)} ppm</div>`;
      } else if (key === 'xco2_uncertainty') {
        const uncertVal = parseFloat(value);
        if (value == null || isNaN(uncertVal)) {
          return '';
        }
        return `<div><strong>XCO2 +/-:</strong> ${uncertVal.toFixed(2)} ppm</div>`;
      } else if (key === 'vor') {
        if (supplementalValues.vorticityValue !== null) {
          return `<div><strong>VOR:</strong> ${(supplementalValues.vorticityValue * 1e6).toFixed(2)} E-6</div>`;
        } else {
          return '';
        }
      } else if (key === 'chl') {
        if (supplementalValues.chlValue !== null && !isNaN(supplementalValues.chlValue)) {
          return `<div><strong>CHL:</strong> ${supplementalValues.chlValue.toFixed(2)} mg/m3</div>`;
        } else {
          return '';
        }
      } else if (key === 'satellite_id') {
        if (value == null || value === '') {
          return '';
        }
        let sourceStr = value;
        if (value == 2) {
          sourceStr = 'OCO2';
        } else if (value == 3) {
          sourceStr = 'OCO3';
        } else if (value == 4) {
          sourceStr = 'SOCAT';
        }
        return `<div><strong>SOURCE:</strong> ${sourceStr}</div>`;
      } else if (key === 'longitude') { 
        return "";  // handled with latitude above, so we don't show longitude separately.
      } else if (key === 'sounding_id') {
        return `<div><strong>ID:</strong> ${value}</div>`;
      } else if (value != null) {
        return `<div><strong>${displayKey}:</strong> ${value}</div>`;
      } else {
        return '';
      }
    };
    

    // Define field order based on show_selection
    let fieldsToShow = [];
    if (show_selection === 'raw') {
      fieldsToShow = ['xco2', 'chl', 'vor', 'DIVIDER', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2_uncertainty', 'xco2_std_15km', 'sounding_id', 'satellite_id'];
    } else if (show_selection === 'raw_avg12') {
      fieldsToShow = ['xco2_avg_12', 'chl', 'vor',  'DIVIDER', 'xco2','latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2_uncertainty', 'xco2_std_15km', 'sounding_id', 'satellite_id'];
    } else if (show_selection === 'raw_avg25' || show_selection === 'co2_max_min') {
      fieldsToShow = ['xco2_avg_25', 'chl', 'vor', 'DIVIDER', 'xco2', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2_uncertainty', 'xco2_std_15km', 'sounding_id', 'satellite_id'];
    } else if (show_selection === 'delta_avg12') {
      fieldsToShow = ['gradient_avg_12', 'chl', 'vor', 'DIVIDER', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2', 'xco2_uncertainty', 'co2_derivative', 'gradient_std_12', 'sounding_id', 'satellite_id'];
    } else if (show_selection === 'delta_avg25' || show_selection === 'delta_avg_25_max_min') {
      fieldsToShow = ['gradient_avg_25', 'chl', 'vor', 'DIVIDER', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2', 'xco2_uncertainty', 'co2_derivative', 'gradient_std_12', 'sounding_id', 'satellite_id'];
    } else if (show_selection === 'delta') {
      fieldsToShow = ['co2_derivative', 'chl', 'vor', 'DIVIDER', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2', 'xco2_uncertainty', 'gradient_std_12', 'sounding_id', 'satellite_id'];
    } else {
      // Fallback to raw if unknown selection
      fieldsToShow = ['xco2', 'chl', 'vor', 'DIVIDER', 'latitude', 'longitude', 'u_wind', 'v_wind', 'orbit', 'xco2_uncertainty', 'sounding_id', 'satellite_id'];
    }
    
    // Build popup HTML with ordered fields
    fieldsToShow.forEach(fieldKey => {
      if (fieldKey === 'DIVIDER') {
        popupHTML += '<div style="border-top: 1px solid #ccc; margin: 5px 0;"></div>';
      } else if (props[fieldKey]) {
        popupHTML += formatField(fieldKey, props[fieldKey]);
      } else {
        // If the field is not present in props, we can choose to display "N/A" or skip it.
        popupHTML += formatField(fieldKey, null);
      }
    });
   
    popupContainer.innerHTML = popupHTML;
    
    // // Add vorticity controls
    // const vorticityControls = document.createElement('div');
    // vorticityControls.style.borderTop = '1px solid #ccc';
    // vorticityControls.style.marginTop = '8px';
    // vorticityControls.style.paddingTop = '8px';
    // vorticityControls.style.display = 'flex';
    // vorticityControls.style.gap = '5px';
    // vorticityControls.style.alignItems = 'center';
    
    // const vorticityBtn = document.createElement('button');
    // vorticityBtn.textContent = 'Show Vorticity';
    // vorticityBtn.style.padding = '4px 8px';
    // vorticityBtn.style.cursor = 'pointer';
    // vorticityBtn.style.backgroundColor = '#e0e0e0';
    // vorticityBtn.style.border = '1px solid #999';
    // vorticityBtn.style.borderRadius = '3px';
    
    // const vorticityInput = document.createElement('input');
    // vorticityInput.type = 'number';
    // vorticityInput.value = '6';
    // vorticityInput.style.width = '40px';
    // vorticityInput.style.padding = '2px';
    // vorticityInput.min = '0.5';
    // vorticityInput.step = '0.5';
    
    // const vorticityLabel = document.createElement('label');
    // vorticityLabel.textContent = '° area';
    // vorticityLabel.style.marginLeft = '2px';
    
    // vorticityControls.appendChild(vorticityBtn);
    // vorticityControls.appendChild(vorticityInput);
    // vorticityControls.appendChild(vorticityLabel);
    // popupContainer.appendChild(vorticityControls);
    
    // // Store reference to feature for vorticity button
    // vorticityBtn.dataset.latitude = props.latitude;
    // vorticityBtn.dataset.longitude = props.longitude;
    // vorticityBtn.dataset.soundingId = props.sounding_id;
    // vorticityBtn.dataset.areaInput = vorticityInput.value;
    
    // vorticityBtn.addEventListener('click', () => {
    //   const lat = parseFloat(vorticityBtn.dataset.latitude);
    //   const lon = parseFloat(vorticityBtn.dataset.longitude);
    //   const soundingId = vorticityBtn.dataset.soundingId;
    //   const area = parseFloat(vorticityInput.value);
    //   loadVorticityHeatmap(lat, lon, soundingId, area, show_location);
    // });
    
    // vorticityInput.addEventListener('change', () => {
    //   vorticityBtn.dataset.areaInput = vorticityInput.value;
    // });
    
    popupContainer.style.userSelect = 'text';
    popupContainer.style.webkitUserSelect = 'text';
    popupContainer.style.cursor = 'text';
    overlay.setPosition(coordinate);
    popupContainer.style.display = 'block';
  };

  // Helper function to add a clicked point
  const addClickedPoint = (lonlat, coords) => {
    const label = `${lonlat[1].toFixed(2)}, ${lonlat[0].toFixed(2)}`;
    
    // Create circle feature
    const circleFeature = new Feature({
      geometry: new Point(coords),
      isClickedPoint: true,
      clickedCoords: coords
    });
    
    circleFeature.setStyle(new Style({
      image: new Circle({
        fill: new Fill({color: 'rgba(255, 255, 255, 0)'}),
        stroke: new Stroke({color: '#000000', width: 3}),
        radius: 7.5 // 15 pixels diameter = 7.5 pixels radius
      })
    }));
    
    // Create label feature
    const labelFeature = new Feature({
      geometry: new Point(coords), // Same location as circle
      isClickedPoint: true,
      parentCircle: circleFeature
    });
    
    labelFeature.setStyle(new Style({
      text: new Text({
        text: label,
        fontSize: '11px',
        fontFamily: 'Arial',
        fill: new Fill({color: '#000000'}),
        stroke: new Stroke({color: '#ffffff', width: 2}),
        offsetY: 25 // 25 pixels below the circle
      })
    }));
    
    // Store references to linked features
    circleFeature.set('labelFeature', labelFeature);
    labelFeature.set('circleFeature', circleFeature);
    
    clickedPointsSource.addFeature(circleFeature);
    clickedPointsSource.addFeature(labelFeature);
    clickedPoints.push(circleFeature);
    syncUrlWithCurrentState();
  };

  const closePopup = () => {
    popupContainer.style.display = 'none';
    selectedPopupFeature = null;
    isPopupPersisted = false;
  };

  const showArrowPopupForFeature = (feature, coordinate) => {
    const props = feature.getProperties();
    const arrowType = props.arrowType === 'current' ? 'current' : 'wind';

    const speed = arrowType === 'current'
      ? toFiniteNumber(props.current_speed_kmh)
      : toFiniteNumber(props.wind_speed_kmh);
    const uValue = arrowType === 'current'
      ? toFiniteNumber(props.u_current)
      : toFiniteNumber(props.u_wind);
    const vValue = arrowType === 'current'
      ? toFiniteNumber(props.v_current)
      : toFiniteNumber(props.v_wind);

    const speedLabel = speed === null ? 'N/A' : `${speed.toFixed(1)} km/hour`;
    const uLabel = uValue === null ? 'N/A' : uValue.toFixed(3);
    const vLabel = vValue === null ? 'N/A' : vValue.toFixed(3);
    const title = arrowType === 'current' ? 'Current Arrow' : 'Wind Arrow';
    const uName = arrowType === 'current' ? 'u_current' : 'u_wind';
    const vName = arrowType === 'current' ? 'v_current' : 'v_wind';

    popupContainer.innerHTML = `
      <div style="position: relative; padding-right: 20px;">
        <button id="arrow-popup-close" type="button" style="position: absolute; top: -2px; right: -2px; width: 18px; height: 18px; border: 1px solid #888; border-radius: 3px; background: #f3f3f3; cursor: pointer; line-height: 14px;">&times;</button>
        <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
        <div><strong>Speed:</strong> ${speedLabel}</div>
        <div><strong>${uName}:</strong> ${uLabel}</div>
        <div><strong>${vName}:</strong> ${vLabel}</div>
      </div>
    `;

    popupContainer.querySelector('#arrow-popup-close')?.addEventListener('click', (event) => {
      event.stopPropagation();
      closePopup();
    });

    overlay.setPosition(coordinate);
    popupContainer.style.display = 'block';
    selectedPopupFeature = feature;
    isPopupPersisted = true;
  };

  // Popup and click functionality
  map.on('pointermove', (e) => {
    if (measureTool?.shouldBlockMapInteractions() || graphTool?.shouldBlockMapInteractions()) {
      return;
    }

    const pixel = map.getEventPixel(e.originalEvent);
    const target = map.getTarget();
    if (target && target.style) {
      target.style.cursor = '';
    }
    
    let hitDataFeature = null;
    let hitVorticityFeature = null;
    let hitChlFeature = null;
    let hitBgaFeature = null;
    let hitMblhFeature = null;
    map.forEachFeatureAtPixel(pixel, (feature) => {
      if (feature.get('isMeasureToolFeature') || feature.get('isGraphRectangle') || feature.get('isValleyWidthLine')) {
        return;
      }
      if (!hitVorticityFeature && feature.get('isVorticityPoint')) {
        hitVorticityFeature = feature;
      }
      if (!hitChlFeature && feature.get('isChlPoint')) {
        hitChlFeature = feature;
      }
      if (!hitBgaFeature && feature.get('isBgaPoint')) {
        hitBgaFeature = feature;
      }
      if (!hitMblhFeature && feature.get('isMblhPoint')) {
        hitMblhFeature = feature;
      }
      // Only show popup for data features (not clicked points or labels)
      if (!feature.get('isClickedPoint') && !feature.get('isVorticityPoint') && !feature.get('isArrow') && !feature.get('isChlPoint') && !feature.get('isBgaPoint') && !feature.get('isMblhPoint') && !feature.get('isOrbitLabel')) {
        hitDataFeature = feature;
      }
    });

    if (hitChlFeature) {
      updateChlHoverIndicator(hitChlFeature.get('chlValue'), hitChlFeature.get('chlNorm'));
    } else {
      clearChlHoverIndicator();
    }

    if (hitBgaFeature && show_bga) {
      updateBgaHoverIndicator(hitBgaFeature.get('bgaValue'));
    } else {
      clearBgaHoverIndicator();
    }

    if (hitMblhFeature && show_mblh) {
      updateMblhHoverIndicator(hitMblhFeature.get('mblhValue'));
    } else {
      clearMblhHoverIndicator();
    }

    // Show the hovered point's value position on the main spectrum and hide it when no point is hovered.
    if (hitDataFeature && current_spectrum_metric_range) {
      const hoverMetricValue = getRowMetricValue(hitDataFeature.getProperties(), show_selection);
      if (hoverMetricValue !== null) {
        updateMainSpectrumIndicator(hoverMetricValue, current_spectrum_metric_range, show_split_color_spectrum);
        updateMainSpectrumHoverValue(hoverMetricValue, current_spectrum_metric_range, show_selection);
      } else {
        clearMainSpectrumIndicator();
        clearMainSpectrumHoverValue();
      }
    } else {
      clearMainSpectrumIndicator();
      clearMainSpectrumHoverValue();
    }

    // Show hovered vorticity point position on the vorticity spectrum (clamped at edges when out of range).
    if (hitVorticityFeature && current_vorticity_range) {
      const hoverVorticityValue = toFiniteNumber(hitVorticityFeature.get('vorticity'));
      if (hoverVorticityValue !== null) {
        updateVorticitySpectrumIndicator(hoverVorticityValue, current_vorticity_range);
        updateVorticityHoverValue(hoverVorticityValue);
      } else {
        clearVorticitySpectrumIndicator();
        clearVorticityHoverValue();
      }
    } else {
      clearVorticitySpectrumIndicator();
      clearVorticityHoverValue();
    }
    
    // console.log(`pointermove ${isPopupPersisted}`);
    // Show popup on hover only if no popup is persisted
    if (hitDataFeature && !isPopupPersisted) {
      showPopupForFeature(hitDataFeature, e.coordinate, false);
      selectedPopupFeature = hitDataFeature;
      if (target && target.style) {
        target.style.cursor = 'pointer';
      }
    } else if (!hitDataFeature && !isPopupPersisted) {
      // Hide popup only if it's not persisted
      popupContainer.style.display = 'none';
      selectedPopupFeature = null;
    }
  });

  map.on('click', async (e) => {
    if (graphTool?.consumeMapClick()) {
      return;
    }
    if (graphTool?.shouldBlockMapInteractions()) {
      return;
    }
    if (measureTool?.shouldBlockMapInteractions()) {
      return;
    }

    const pixel = map.getEventPixel(e.originalEvent);

    // Resolve the feature to act on for this pixel, always preferring a data/clicked point over any arrow underneath it.
    const pickClickFeatureAtPixel = () => {
      let arrowFeature = null;
      let otherFeature = null;
      map.forEachFeatureAtPixel(pixel, (feature) => {
        if (feature.get('isChlPoint') || feature.get('isMblhPoint') || feature.get('isOrbitLabel') || feature.get('isGraphRectangle') || feature.get('isValleyWidthLine')) {
          return;
        }
        if (feature.get('isArrow')) {
          if (!arrowFeature) arrowFeature = feature;
          return;
        }
        otherFeature = feature;
      });
      return otherFeature || arrowFeature;
    };

    // Arrow popups are handled separately to avoid generic N/A data popups, but only when no data/clicked point sits on top of the arrow.
    const clickFeature = pickClickFeatureAtPixel();
    if (clickFeature?.get('isArrow')) {
      showArrowPopupForFeature(clickFeature, e.coordinate);
      return;
    }

    // Measure lines should be removable in normal mode even when other overlays exist at the same pixel.
    const hitMeasureFeature = map.forEachFeatureAtPixel(pixel, (feature) => {
      if (feature.get('isMeasureToolFeature')) {
        return feature;
      }
      return undefined;
    });
    if (hitMeasureFeature && measureTool?.removeLineByFeature(hitMeasureFeature)) {
      popupContainer.style.display = 'none';
      selectedPopupFeature = null;
      isPopupPersisted = false;
      syncUrlWithCurrentState();
      return;
    }

    // Check if clicking inside popup
    if (popupContainer.style.display !== 'none' ) {
      // If popup is already persisted, dismiss it
      if (isPopupPersisted) {
        popupContainer.style.display = 'none';
        selectedPopupFeature = null;
        isPopupPersisted = false;
      } else {
        // If popup was just shown on hover, persist it by clicking
        // but only if shift is held to avoid accidental clicks when trying to interact with the popup
        isPopupPersisted = true;
        // update cloud layer date based on the selected popup feature
        if (selectedPopupFeature) {
          const props = selectedPopupFeature.getProperties();
          const sounding_id = props.sounding_id || 'N/A';
          const clickedDate = parseDateFromSoundingId(sounding_id);
          if (clickedDate) {
            await Promise.all([
              ensurePopupVorticityDataForDate({
                dateValue: clickedDate,
                location: show_location,
                fallbackDate: earliest_displayed_orbit_date
              }),
              ensurePopupChlDataForDate({
                dateValue: clickedDate,
                location: show_location
              })
            ]);
            // Clicking a point moves the shared Clouds/Currents/Vorticity/Wind/Chl date and reloads whichever are enabled.
            await refreshDateDependentOverlays(clickedDate, {emitNoDateMessage: false});
            showPopupForFeature(selectedPopupFeature, e.coordinate, true);
            isPopupPersisted = true;
          }
        }

      }
      return;
    }
    
    let hitFeature = clickFeature;

    if (hitFeature) {
      if (measureTool?.removeLineByFeature(hitFeature)) {
        popupContainer.style.display = 'none';
        selectedPopupFeature = null;
        isPopupPersisted = false;
        syncUrlWithCurrentState();
        return;
      }

      if (hitFeature.get('isClickedPoint')) {
        // Clicking on a clicked circle or its label - remove both
        const circleFeature = hitFeature.get('circleFeature') || hitFeature;
        const labelFeature = circleFeature.get('labelFeature');
        
        // Remove both circle and label from source
        if (labelFeature) {
          clickedPointsSource.removeFeature(labelFeature);
        }
        clickedPointsSource.removeFeature(circleFeature);
        clickedPoints = clickedPoints.filter(p => p !== circleFeature);
        popupContainer.style.display = 'none';
        selectedPopupFeature = null;
        isPopupPersisted = false;
        syncUrlWithCurrentState();
      } else {
        const soundingId = hitFeature.get('sounding_id');
        const clickedDate = parseDateFromSoundingId(soundingId);
        if (clickedDate) {
          await Promise.all([
            ensurePopupVorticityDataForDate({
              dateValue: clickedDate,
              location: show_location,
              fallbackDate: earliest_displayed_orbit_date
            }),
            ensurePopupChlDataForDate({
              dateValue: clickedDate,
              location: show_location
            })
          ]);

          // Clicking a point moves the shared Clouds/Currents/Vorticity/Wind/Chl date and reloads whichever are enabled.
          await refreshDateDependentOverlays(clickedDate, {emitNoDateMessage: false});
        }

        // Clicking on a data point - show popup and persist it after supplemental data load.
        showPopupForFeature(hitFeature, e.coordinate, true);
        selectedPopupFeature = hitFeature;
        isPopupPersisted = true;
      }
    } else if (clickedPoints.length < maxClickedPoints && e.originalEvent.shiftKey) {  // only add new point if shift is held to avoid accidental clicks, and limit total number of clicked points
      // Clicking on empty map - add a new circle
      const lonlat = toLonLat(e.coordinate);
      addClickedPoint(lonlat, e.coordinate);
      // Clear any non-persisted hover popup
      if (!isPopupPersisted) {
        popupContainer.style.display = 'none';
        selectedPopupFeature = null;
      }
    }
  });

}

const startupQueryConfig = parseStartupUrlQuery();
const keyForm = document.getElementById('key-form');
const keyDialog = document.querySelector('#map dialog');

if (startupQueryConfig.hasAnyParams) {
  if (keyDialog?.open) {
    keyDialog.close();
  }
  keyDialog?.remove();
  showMap('', startupQueryConfig);
} else if (keyForm) {
  keyForm.addEventListener('submit', (event) => {
    event.preventDefault();
    if (keyDialog?.open) {
      keyDialog.close();
    }
    keyDialog?.remove();
    showMap(event.target.elements['key'].value, null);
  });
}
